import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Building2, CheckCircle2, XCircle, CalendarPlus, Loader2, AlertCircle, Users as UsersIcon, FileText, Briefcase, LogOut, Plus, Pencil, Trash2, Sparkles, UserCog, RotateCcw, Archive, Mail, Eye, Send, Wallet, Inbox as InboxIcon, Brain, Save, Search, SlidersHorizontal, Link2 } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Tooltip, CartesianGrid } from "recharts";
import { api, formatFileSize } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { DatePicker } from "@/components/DatePicker";

const STATUS_STYLES = {
  pending: { bg: "bg-amber-50", text: "text-amber-700", border: "border-amber-200", label: "In attesa" },
  accepted_awaiting_data: { bg: "bg-sky-50", text: "text-sky-700", border: "border-sky-200", label: "Attesa dati" },
  data_submitted: { bg: "bg-violet-50", text: "text-violet-700", border: "border-violet-200", label: "Da verificare" },
  active: { bg: "bg-emerald-50", text: "text-emerald-700", border: "border-emerald-200", label: "Attiva" },
  suspended: { bg: "bg-slate-100", text: "text-slate-600", border: "border-slate-300", label: "Sospesa" },
  expired: { bg: "bg-rose-50", text: "text-rose-700", border: "border-rose-200", label: "Scaduta" },
  deleted: { bg: "bg-rose-100", text: "text-rose-800", border: "border-rose-300", label: "Nel cestino" },
};

// Helper: somma giorni a una data e restituisce YYYY-MM-DD
const addDaysISO = (baseDate, days) => {
  const d = new Date(baseDate);
  d.setDate(d.getDate() + days);
  return d.toISOString().slice(0, 10);
};
// Min date (oggi+1) per il datepicker
const tomorrowISO = () => {
  const d = new Date();
  d.setDate(d.getDate() + 1);
  return d.toISOString().slice(0, 10);
};

export default function SuperAdminDashboard() {
  const nav = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { logout } = useAuth();
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [approveTarget, setApproveTarget] = useState(null);
  const [extendTarget, setExtendTarget] = useState(null);
  const [trialDays, setTrialDays] = useState(30);
  const [extendUntil, setExtendUntil] = useState("");
  const [createOpen, setCreateOpen] = useState(false);
  const [createForm, setCreateForm] = useState({
    agency_name: "", owner_name: "", owner_email: "", owner_password: "",
    phone: "", notes: "", trial_days: 30, auto_activate: true,
  });
  const [editTarget, setEditTarget] = useState(null);
  const [editForm, setEditForm] = useState({
    agency_name: "", owner_name: "", owner_email: "", owner_password: "", phone: "", notes: "", activated_at: "", expires_at: "", is_demo: false,
  });
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteStep, setDeleteStep] = useState(1); // 1=warning, 2=OTP input
  const [deleteOtp, setDeleteOtp] = useState("");
  const [deleteCounts, setDeleteCounts] = useState(null);
  // Anteprima notifica email (sospensione/riattivazione/...)
  // shape: { action: "suspend"|"reactivate", agency, subject, html, target_email, busy: bool }
  const [notifyPreview, setNotifyPreview] = useState(null);
  const [profileOpen, setProfileOpen] = useState(false);
  const [profile, setProfile] = useState(null);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [reviewTarget, setReviewTarget] = useState(null);
  const [finalizeDays, setFinalizeDays] = useState(30);
  const [viewMode, setViewMode] = useState("active"); // "active" | "trash"
  const [inboxOpen, setInboxOpen] = useState(false);
  const [inboxSettings, setInboxSettings] = useState(null);
  const [inboxPassChanged, setInboxPassChanged] = useState(false);
  const [inboxDiagnose, setInboxDiagnose] = useState(null);
  const [inboxDiagBusy, setInboxDiagBusy] = useState(false);
  // 🆕 Salute Inbox Agenzie (pannello controllo tecnico)
  const [inboxHealthOpen, setInboxHealthOpen] = useState(false);
  const [inboxHealth, setInboxHealth] = useState(null);
  const [inboxHealthLoading, setInboxHealthLoading] = useState(false);
  const [inboxHealthFilter, setInboxHealthFilter] = useState("all");
  const openInboxHealth = async () => {
    setInboxHealthOpen(true);
    setInboxHealthLoading(true);
    try {
      const { data } = await api.get("/superadmin/inbox-health");
      setInboxHealth(data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore caricamento salute Inbox"); }
    finally { setInboxHealthLoading(false); }
  };
  const [aiLimitsTarget, setAiLimitsTarget] = useState(null);
  const [aiLimitsForm, setAiLimitsForm] = useState({ daily: 50, monthly: 1000, annual: 10000 });
  const [aiLimitsUsage, setAiLimitsUsage] = useState({ daily: 0, monthly: 0, annual: 0 });
  // 🆕 Storage limit per agenzia (in MB) + usage snapshot
  const [storageLimitMb, setStorageLimitMb] = useState(500);
  const [storageUsageInfo, setStorageUsageInfo] = useState(null);
  const [aiLimitsLoading, setAiLimitsLoading] = useState(false);
  const [aiUsageHistory, setAiUsageHistory] = useState([]);
  const [resLimitsTarget, setResLimitsTarget] = useState(null);
  const [resLimitsLoading, setResLimitsLoading] = useState(false);
  const [resLimitsData, setResLimitsData] = useState(null);
  const [resLimitsForm, setResLimitsForm] = useState({ max_clients: "", max_policies: "", max_users: "", plan_id: "", storage_limit_mb: "" });
  const [pricingPlans, setPricingPlans] = useState([]);
  // SA sensitive OTP (per modifica owner_email / owner_password)
  const [saOtpOpen, setSaOtpOpen] = useState(false);
  const [saOtpStep, setSaOtpStep] = useState("idle"); // "idle" | "sent"
  const [saOtpCode, setSaOtpCode] = useState("");
  const [saOtpBusy, setSaOtpBusy] = useState(false);
  const [saOtpRecipient, setSaOtpRecipient] = useState("");
  // Payload pending — quando il backend chiede l'OTP, salviamo il payload e lo rieseguiamo dopo la verifica
  const [saPendingPayload, setSaPendingPayload] = useState(null);
  const [busy, setBusy] = useState(false);
  // 🆕 30/07/2026 — Accessi demo multipli
  const [demoAccesses, setDemoAccesses] = useState([]);
  const [demoAccessDialog, setDemoAccessDialog] = useState(null); // null | { form: {...} }
  const [demoAccessBusy, setDemoAccessBusy] = useState(false);
  const [lastDemoTempPwd, setLastDemoTempPwd] = useState(null); // se send_email=false
  // 🆕 Feb 2026 — Dettaglio accessi (SuperAdmin monitor login)
  const [accessLogTarget, setAccessLogTarget] = useState(null); // agency object | null
  const [accessLog, setAccessLog] = useState(null); // { online_users, recent_logins }
  const [accessLogLoading, setAccessLogLoading] = useState(false);
  const openAccessLog = async (agency) => {
    setAccessLogTarget(agency);
    setAccessLog(null);
    setAccessLogLoading(true);
    try {
      const { data } = await api.get(`/superadmin/agencies/${agency.id}/access-log`);
      setAccessLog(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore caricamento accessi");
    } finally {
      setAccessLogLoading(false);
    }
  };

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/superadmin/agencies${viewMode === "trash" ? "?trash=true" : ""}`);
      setList(data || []);
    }
    catch (e) { toast.error("Errore caricamento"); }
    finally { setLoading(false); }
  };
  useEffect(() => {
    load();
  }, [viewMode]);
  useEffect(() => {
    // Pre-carica anagrafica per mostrare email in header
    api.get("/superadmin/profile").then(({ data }) => setProfile(data)).catch(() => {});
  }, []);

  // Gestisci openInbox / openProfile da query string (sidebar)
  useEffect(() => {
    if (searchParams.get("openInbox") === "1") {
      openInboxSettings();
      const sp = new URLSearchParams(searchParams);
      sp.delete("openInbox");
      setSearchParams(sp, { replace: true });
    }
    if (searchParams.get("openInboxHealth") === "1") {
      openInboxHealth();
      const sp = new URLSearchParams(searchParams);
      sp.delete("openInboxHealth");
      setSearchParams(sp, { replace: true });
    }
    if (searchParams.get("openProfile") === "1") {
      openProfile();
      const sp = new URLSearchParams(searchParams);
      sp.delete("openProfile");
      setSearchParams(sp, { replace: true });
    }
  }, [searchParams]);

  const doApprove = async () => {
    if (!approveTarget) return;
    setBusy(true);
    try {
      await api.post(`/superadmin/agencies/${approveTarget.id}/approve`, { trial_days: Number(trialDays) || 30 });
      toast.success(`${approveTarget.name} attivata per ${trialDays} giorni`);
      setApproveTarget(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
    finally { setBusy(false); }
  };

  // Nuovo flusso 3-step
  const acceptRequest = async (ag) => {
    if (!window.confirm(`Accettare la richiesta di "${ag.name}"?\n\nVerrà inviata un'email all'owner con il link per compilare i dati anagrafici completi.`)) return;
    try {
      const { data } = await api.post(`/superadmin/agencies/${ag.id}/accept-request`);
      toast.success("Richiesta accettata. Link inviato via email.");
      window.prompt("Link onboarding (copialo per condividerlo manualmente, es. WhatsApp):", data.onboarding_url);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
  };
  const resendOnboarding = async (ag) => {
    try {
      const { data } = await api.post(`/superadmin/agencies/${ag.id}/resend-onboarding`);
      toast.success("Link reinviato via email");
      window.prompt("Nuovo link onboarding:", data.onboarding_url);
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
  };
  const finalizeActivation = async () => {
    if (!reviewTarget) return;
    setBusy(true);
    try {
      await api.post(`/superadmin/agencies/${reviewTarget.id}/finalize-activation`, { trial_days: Number(finalizeDays) || 30 });
      toast.success(`${reviewTarget.name} attivata definitivamente`);
      setReviewTarget(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
    finally { setBusy(false); }
  };


  const doExtend = async () => {
    if (!extendTarget || !extendUntil) return;
    setBusy(true);
    try {
      await api.post(`/superadmin/agencies/${extendTarget.id}/extend`, { until: extendUntil });
      toast.success(`Rinnovo applicato fino al ${new Date(extendUntil).toLocaleDateString("it-IT")}`);
      setExtendTarget(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
    finally { setBusy(false); }
  };

  // 🆕 Feb 2026 — SuperAdmin: cambia slug agenzia (preserva vecchi link via slug_aliases)
  const changeAgencySlug = async (ag) => {
    const newSlug = window.prompt(
      `Nuovo slug per "${ag.name}"?\n\n` +
      `Attuale: ${ag.slug}\n\n` +
      `Regole: solo lettere minuscole, numeri e trattini (es. "amoroso-assicurazioni").\n` +
      `Il vecchio slug verrà conservato come alias, quindi i link pubblici già inviati ai clienti continueranno a funzionare.`,
      ag.slug
    );
    if (!newSlug || newSlug.trim().toLowerCase() === (ag.slug || "").trim().toLowerCase()) return;
    try {
      const { data } = await api.post(`/superadmin/agencies/${ag.id}/change-slug`, { new_slug: newSlug.trim() });
      toast.success(`Slug cambiato: ${data.old_slug} → ${data.new_slug} · Alias attivi: ${(data.slug_aliases || []).length}`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
  };


  // Apre dialog estensione e propone lo stesso periodo della prima attivazione
  const openExtend = (ag) => {
    setExtendTarget(ag);
    // 1) Se l'agenzia ha `trial_days` memorizzato (periodo iniziale) → usa quello
    // 2) Altrimenti calcola differenza tra activated_at e primo expires_at
    // 3) Fallback: 365 giorni
    let periodDays = 365;
    if (ag.trial_days && Number(ag.trial_days) > 0) {
      periodDays = Number(ag.trial_days);
    } else if (ag.activated_at && ag.expires_at) {
      try {
        const a = new Date(ag.activated_at);
        const e = new Date(ag.expires_at);
        const diff = Math.round((e - a) / (1000 * 60 * 60 * 24));
        if (diff > 0) periodDays = diff;
      } catch { /* ignore */ }
    }
    // Base: scadenza attuale se futura, altrimenti oggi
    let base = new Date();
    if (ag.expires_at) {
      try {
        const e = new Date(ag.expires_at);
        if (e > base) base = e;
      } catch { /* ignore */ }
    }
    setExtendUntil(addDaysISO(base, periodDays));
  };

  const doCreate = async () => {
    const f = createForm;
    if (!f.agency_name.trim() || !f.owner_name.trim() || !f.owner_email.trim() || !f.owner_password) {
      toast.error("Compila tutti i campi obbligatori"); return;
    }
    if (f.owner_password.length < 8) { toast.error("Password: minimo 8 caratteri"); return; }
    setBusy(true);
    try {
      await api.post(`/superadmin/agencies`, {
        ...f,
        trial_days: Number(f.trial_days) || 30,
      });
      toast.success(`Agenzia "${f.agency_name}" creata${f.auto_activate ? " e attivata" : ""}`);
      setCreateOpen(false);
      setCreateForm({ agency_name: "", owner_name: "", owner_email: "", owner_password: "", phone: "", notes: "", trial_days: 30, auto_activate: true });
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore creazione"); }
    finally { setBusy(false); }
  };

  const [emailSearch, setEmailSearch] = useState("");
  const [emailSearchResults, setEmailSearchResults] = useState(null);
  const [emailSearchBusy, setEmailSearchBusy] = useState(false);

  useEffect(() => {
    const q = emailSearch.trim();
    if (q.length < 2) { setEmailSearchResults(null); return; }
    setEmailSearchBusy(true);
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get(`/superadmin/email-search?q=${encodeURIComponent(q)}&limit_per_collection=15`);
        setEmailSearchResults(data);
      } catch (e) {
        toast.error(e?.response?.data?.detail || "Errore ricerca");
        setEmailSearchResults(null);
      } finally {
        setEmailSearchBusy(false);
      }
    }, 350);
    return () => clearTimeout(t);
  }, [emailSearch]);

  const openEdit = (ag) => {
    setEditTarget(ag);
    // 🆕 Carica accessi demo se l'agenzia è demo
    if (ag?.is_demo && ag?.id) {
      api.get(`/superadmin/agencies/${ag.id}/demo-accesses`)
        .then((r) => setDemoAccesses(r.data || []))
        .catch(() => setDemoAccesses([]));
    } else {
      setDemoAccesses([]);
    }
    setLastDemoTempPwd(null);
    setEditForm({
      agency_name: ag.name || "",
      owner_name: ag.owner_name || "",
      owner_email: ag.owner_email || "",
      owner_password: "",
      phone: ag.owner_phone || "",
      notes: ag.notes || "",
      activated_at: ag.activated_at ? new Date(ag.activated_at).toISOString().slice(0, 10) : "",
      expires_at: ag.expires_at ? new Date(ag.expires_at).toISOString().slice(0, 10) : "",
      otp_recipient_email: ag.otp_recipient_email || "",
      is_demo: !!ag.is_demo,
    });
  };

  const openAiLimits = async (ag) => {
    setAiLimitsTarget(ag);
    setAiLimitsLoading(true);
    try {
      const [{ data }, { data: hist }, storageResp, agencyDoc] = await Promise.all([
        api.get(`/superadmin/agencies/${ag.id}/ai-limits`),
        api.get(`/superadmin/agencies/${ag.id}/ai-usage-history?days=30`).catch(() => ({ data: { series: [] } })),
        api.get(`/superadmin/agencies/${ag.id}/storage-usage`).catch(() => null),
        api.get(`/superadmin/agencies/${ag.id}`).catch(() => null),
      ]);
      setAiLimitsForm({
        daily: data?.limits?.daily ?? 50,
        monthly: data?.limits?.monthly ?? 1000,
        annual: data?.limits?.annual ?? 10000,
      });
      setAiLimitsUsage(data?.used || { daily: 0, monthly: 0, annual: 0 });
      setAiUsageHistory(
        (hist?.series || []).map((d) => ({
          date: d.date,
          label: d.date ? `${d.date.slice(8, 10)}/${d.date.slice(5, 7)}` : "",
          count: d.count || 0,
        }))
      );
      // 🆕 Storage: usage + limite corrente
      setStorageUsageInfo(storageResp?.data || null);
      const currentLimit = agencyDoc?.data?.storage_limit_mb ?? storageResp?.data?.limit_mb ?? 500;
      setStorageLimitMb(currentLimit);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore caricamento limiti");
      setAiLimitsTarget(null);
    } finally {
      setAiLimitsLoading(false);
    }
  };

  const doSaveAiLimits = async () => {
    if (!aiLimitsTarget) return;
    const f = aiLimitsForm;
    const daily = Number(f.daily);
    const monthly = Number(f.monthly);
    const annual = Number(f.annual);
    if (!Number.isFinite(daily) || !Number.isFinite(monthly) || !Number.isFinite(annual) || daily < 0 || monthly < 0 || annual < 0) {
      toast.error("Valori non validi (usa numeri ≥ 0; 0 = illimitato)");
      return;
    }
    setBusy(true);
    try {
      await api.put(`/superadmin/agencies/${aiLimitsTarget.id}/ai-limits`, { daily, monthly, annual });
      // 🆕 Salva anche il limite di storage
      const storageLimit = Number(storageLimitMb);
      if (Number.isFinite(storageLimit) && storageLimit >= 1) {
        try {
          await api.put(`/admin/agencies/${aiLimitsTarget.id}/storage-limit`, { storage_limit_mb: storageLimit });
        } catch (errS) {
          toast.warning(`Storage limit non aggiornato: ${errS?.response?.data?.detail || errS.message}`);
        }
      }
      toast.success("Limiti IA e storage aggiornati");
      setAiLimitsTarget(null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore salvataggio");
    } finally {
      setBusy(false);
    }
  };

  const openResLimits = async (ag) => {
    setResLimitsTarget(ag);
    setResLimitsLoading(true);
    try {
      const [{ data }, plans] = await Promise.all([
        api.get(`/superadmin/agencies/${ag.id}/resource-limits`),
        api.get("/superadmin/pricing-plans").catch(() => ({ data: [] })),
      ]);
      setResLimitsData(data);
      setPricingPlans(plans.data || []);
      setResLimitsForm({
        max_clients: data?.override?.max_clients ?? "",
        max_policies: data?.override?.max_policies ?? "",
        max_users: data?.override?.max_users ?? "",
        plan_id: ag.plan_id || ag.requested_plan_id || "",
        storage_limit_mb: data?.storage_limit_mb ?? "",
      });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore caricamento limiti");
      setResLimitsTarget(null);
    } finally {
      setResLimitsLoading(false);
    }
  };

  const doSaveResLimits = async () => {
    if (!resLimitsTarget) return;
    const f = resLimitsForm;
    const parse = (v) => {
      if (v === "" || v === null || v === undefined) return null;
      const n = Number(v);
      if (!Number.isFinite(n) || n < 0) return "INVALID";
      return n;
    };
    const mc = parse(f.max_clients);
    const mp = parse(f.max_policies);
    const mu = parse(f.max_users);
    if (mc === "INVALID" || mp === "INVALID" || mu === "INVALID") {
      toast.error("Valori non validi (numeri ≥ 0; vuoto/0 = illimitato)");
      return;
    }
    setBusy(true);
    try {
      const storagePayload = f.storage_limit_mb === "" || f.storage_limit_mb == null
        ? null
        : Number(f.storage_limit_mb);
      if (storagePayload !== null && (!Number.isFinite(storagePayload) || storagePayload < 1)) {
        toast.error("Limite storage non valido (>= 1 MB)");
        setBusy(false);
        return;
      }
      await api.put(`/superadmin/agencies/${resLimitsTarget.id}/resource-limits`, {
        max_clients: mc,
        max_policies: mp,
        max_users: mu,
        plan_id: f.plan_id || null,
        storage_limit_mb: storagePayload,
      });
      toast.success("Limiti aggiornati");
      setResLimitsTarget(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore salvataggio");
    } finally {
      setBusy(false);
    }
  };

  const resetResLimitsOverride = () => {
    setResLimitsForm((f) => ({ ...f, max_clients: "", max_policies: "", max_users: "" }));
  };

  const submitAgencyEdit = async (payload, sensitiveToken) => {
    const headers = sensitiveToken ? { "X-SA-Sensitive-Otp-Token": sensitiveToken } : undefined;
    await api.put(`/superadmin/agencies/${editTarget.id}`, payload, { headers });
  };

  const doEdit = async () => {
    if (!editTarget) return;
    const f = editForm;
    if (!f.agency_name.trim() || !f.owner_email.trim()) {
      toast.error("Nome agenzia ed email sono obbligatori"); return;
    }
    if (f.owner_password && f.owner_password.length < 8) {
      toast.error("Password: minimo 8 caratteri (o lascia vuoto per non cambiare)"); return;
    }
    setBusy(true);
    try {
      const payload = {
        agency_name: f.agency_name,
        owner_name: f.owner_name,
        owner_email: f.owner_email,
        phone: f.phone,
        notes: f.notes,
      };
      if (f.activated_at && f.activated_at.trim()) {
        payload.activated_at = f.activated_at.trim();
      }
      if (f.expires_at && f.expires_at.trim()) {
        payload.expires_at = f.expires_at.trim();
      }
      if (f.owner_password && f.owner_password.trim()) {
        payload.owner_password = f.owner_password;
      }
      payload.otp_recipient_email = (f.otp_recipient_email || "").trim();
      payload.is_demo = !!f.is_demo;
      const cachedSaOtp = sessionStorage.getItem("sa_sensitive_otp_token") || undefined;
      try {
        await submitAgencyEdit(payload, cachedSaOtp);
        toast.success("Dati agenzia aggiornati");
        setEditTarget(null);
        load();
      } catch (e) {
        if (e?.response?.status === 401 && e?.response?.data?.detail === "SA_OTP_REQUIRED") {
          // Cache token mancante/scaduto: apri dialog OTP
          sessionStorage.removeItem("sa_sensitive_otp_token");
          setSaPendingPayload(payload);
          await requestSaOtp();
          setSaOtpOpen(true);
        } else {
          throw e;
        }
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore aggiornamento");
    } finally {
      setBusy(false);
    }
  };

  const requestSaOtp = async () => {
    setSaOtpBusy(true);
    try {
      const { data } = await api.post("/superadmin/sensitive-otp/request");
      setSaOtpRecipient(data.recipient_masked || "");
      setSaOtpStep("sent");
      setSaOtpCode("");
      toast.success(`Codice OTP inviato a ${data.recipient_masked}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore richiesta OTP");
    } finally {
      setSaOtpBusy(false);
    }
  };

  const verifySaOtpAndRetry = async () => {
    if (!/^\d{6}$/.test(saOtpCode)) {
      toast.error("Inserisci un codice di 6 cifre");
      return;
    }
    setSaOtpBusy(true);
    try {
      const { data } = await api.post("/superadmin/sensitive-otp/verify", { code: saOtpCode });
      sessionStorage.setItem("sa_sensitive_otp_token", data.sensitive_token);
      if (saPendingPayload) {
        await submitAgencyEdit(saPendingPayload, data.sensitive_token);
        toast.success("Dati agenzia aggiornati");
        setEditTarget(null);
        load();
      }
      setSaOtpOpen(false);
      setSaOtpStep("idle");
      setSaPendingPayload(null);
      setSaOtpCode("");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore verifica OTP");
    } finally {
      setSaOtpBusy(false);
    }
  };

  const openDelete = (ag) => {
    setDeleteTarget(ag);
    // 🆕 Sia soft-delete (attiva → cestino) sia hard-delete (cestino → fisico) richiedono OTP.
    // Step 1 mostra warning + invio OTP, step 2 richiede il codice.
    setDeleteStep(1);
    setDeleteOtp("");
    setDeleteCounts(null);
  };
  const doSoftDelete = async () => {
    // 🚨 DEPRECATED — soft-delete ora passa dal flusso OTP unificato (confirmDelete).
    // Mantenuto solo per compatibilità con eventuali chiamate residue.
    if (!deleteTarget) return;
    setBusy(true);
    try {
      const { data } = await api.delete(`/superadmin/agencies/${deleteTarget.id}`);
      toast.success(`Agenzia "${data.deleted_agency}" spostata nel cestino`);
      setDeleteTarget(null); setDeleteStep(0);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
    finally { setBusy(false); }
  };
  const restoreAgency = async (ag) => {
    if (!window.confirm(`Ripristinare "${ag.name}" dal cestino?\nL'agenzia tornerà allo stato precedente. Gli utenti restano disattivati e vanno riattivati manualmente.`)) return;
    try {
      await api.post(`/superadmin/agencies/${ag.id}/restore`);
      toast.success(`"${ag.name}" ripristinata`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
  };
  const requestDeleteOtp = async () => {
    if (!deleteTarget) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/superadmin/agencies/${deleteTarget.id}/request-delete-otp`);
      setDeleteCounts(data.counts || null);
      setDeleteStep(2);
      toast.success(`Codice OTP inviato a ${data.email}`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
    finally { setBusy(false); }
  };
  const confirmDelete = async () => {
    if (!deleteTarget || !deleteOtp.trim()) { toast.error("Inserisci il codice OTP"); return; }
    if (deleteOtp.trim().length !== 6) { toast.error("Il codice OTP deve essere di 6 cifre"); return; }
    setBusy(true);
    try {
      // 🆕 Se l'agenzia è nel cestino → hard-delete (/permanent), altrimenti soft-delete (sposta nel cestino).
      const isTrashed = viewMode === "trash" || deleteTarget.status === "deleted" || !!deleteTarget.deleted_at;
      const endpoint = isTrashed
        ? `/superadmin/agencies/${deleteTarget.id}/permanent?otp=${encodeURIComponent(deleteOtp.trim())}`
        : `/superadmin/agencies/${deleteTarget.id}?otp=${encodeURIComponent(deleteOtp.trim())}`;
      const { data } = await api.delete(endpoint);
      if (isTrashed) {
        toast.success(`Agenzia "${data.deleted_agency || deleteTarget.name}" eliminata definitivamente`);
      } else {
        toast.success(`Agenzia "${data.deleted_agency || deleteTarget.name}" spostata nel cestino`);
      }
      setDeleteTarget(null); setDeleteOtp(""); setDeleteStep(1); setDeleteCounts(null);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore eliminazione"); }
    finally { setBusy(false); }
  };

  const openProfile = async () => {
    setProfileOpen(true);
    try { const { data } = await api.get("/superadmin/profile"); setProfile(data); }
    catch (e) { toast.error("Errore caricamento profilo"); setProfile({}); }
  };
  const saveProfile = async () => {
    if (!profile) return;
    if (!profile.email || !profile.email.trim()) { toast.error("Email di login obbligatoria"); return; }
    setBusy(true);
    try {
      const { data } = await api.put("/superadmin/profile", profile);
      setProfile(data);
      toast.success("Anagrafica salvata");
      setProfileOpen(false);
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
    finally { setBusy(false); }
  };

  // 🆕 Dialog "Email di Sistema" — mostra solo info statiche (SMTP piattaforma via env vars)
  const openInboxSettings = () => setInboxOpen(true);

  const pollForAgency = async (ag) => {
    try {
      const { data } = await api.post(`/superadmin/agencies/${ag.id}/poll-inbox`);
      if (data.agency_new_count > 0) {
        toast.success(`📨 ${data.agency_new_count} nuova/e email per ${ag.name}`);
      } else {
        toast.info(`${ag.name}: nessuna nuova email (totale ciclo: ${data.global_new_count || 0})`);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore polling");
    }
  };

  const doSuspend = (ag) => openNotificationPreview("suspend", ag);
  const doReactivate = (ag) => openNotificationPreview("reactivate", ag);

  // Apre l'anteprima dell'email che verrebbe inviata all'agenzia e lascia decidere al super-admin
  // se procedere con/senza notifica.
  const openNotificationPreview = async (action, ag) => {
    try {
      const { data } = await api.get(`/superadmin/agencies/${ag.id}/notification-preview`, { params: { action } });
      setNotifyPreview({ action, agency: ag, ...data, busy: false });
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore preview email"); }
  };

  // Esegue l'azione (suspend/reactivate) con o senza invio email
  const confirmNotification = async (sendEmail) => {
    if (!notifyPreview) return;
    setNotifyPreview((p) => ({ ...p, busy: true }));
    const { action, agency } = notifyPreview;
    try {
      await api.post(`/superadmin/agencies/${agency.id}/${action}`, null, { params: { send_email: sendEmail } });
      toast.success(
        action === "suspend"
          ? (sendEmail ? `Sospesa — email inviata a ${notifyPreview.target_email}` : "Sospesa senza email")
          : (sendEmail ? `Riattivata — email inviata a ${notifyPreview.target_email}` : "Riattivata senza email")
      );
      setNotifyPreview(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore");
      setNotifyPreview((p) => p ? { ...p, busy: false } : p);
    }
  };

  const counts = {
    total: list.length,
    pending: list.filter((a) => a.status === "pending").length,
    active: list.filter((a) => a.status === "active").length,
    suspended: list.filter((a) => a.status === "suspended").length,
    payment_pending: list.filter((a) => a.payment_pending_at).length,
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="max-w-7xl mx-auto px-3 sm:px-6 py-4 sm:py-8 space-y-4 sm:space-y-6" data-testid="superadmin-dashboard">
      <header className="flex items-start justify-between gap-3 flex-wrap">
        <div className="min-w-0 flex-1">
          <p className="text-xs uppercase tracking-wider text-slate-500">Super-Admin</p>
          <h1 className="font-display text-xl sm:text-2xl lg:text-3xl font-semibold tracking-tight flex items-center gap-2">
            <Building2 className="h-5 w-5 sm:h-7 sm:w-7 text-violet-600 flex-shrink-0" />
            <span className="truncate">Gestione Agenzie SaaS</span>
          </h1>
          <p className="text-xs sm:text-sm text-slate-500 mt-1">Approva nuove agenzie, gestisci scadenze e rinnovi della piattaforma.</p>
        </div>
        <Button
          data-testid="create-agency-btn"
          onClick={() => setCreateOpen(true)}
          className="bg-violet-600 hover:bg-violet-700 text-white text-xs sm:text-sm h-9 sm:h-10"
          size="sm"
        >
          <Plus className="h-4 w-4 sm:mr-1.5" />
          <span className="hidden sm:inline">Crea nuova agenzia</span>
          <span className="sm:hidden">Crea</span>
        </Button>
      </header>

      {/* Ricerca email globale (cross-agenzia) */}
      <Card className="border-violet-200 bg-gradient-to-br from-violet-50/60 to-white">
        <CardContent className="py-3">
          <div className="flex items-center gap-2">
            <Search className="h-5 w-5 text-violet-600 flex-shrink-0" />
            <div className="min-w-0 flex-1">
              <Input
                data-testid="email-global-search"
                value={emailSearch}
                onChange={(e) => setEmailSearch(e.target.value)}
                placeholder="🔍 Cerca email, codice fiscale, partita IVA o telefono in tutto il gestionale…"
                className="border-violet-300 focus-visible:ring-violet-500"
              />
            </div>
            {emailSearchBusy && <Loader2 className="h-4 w-4 animate-spin text-violet-600" />}
            {emailSearch && (
              <button
                onClick={() => setEmailSearch("")}
                className="text-xs text-slate-500 hover:text-slate-700 underline"
                data-testid="email-global-search-clear"
              >
                Pulisci
              </button>
            )}
          </div>
          {emailSearchResults && (
            <div className="mt-3 rounded-md border border-violet-200 bg-white max-h-80 overflow-y-auto" data-testid="email-global-search-results">
              <div className="px-3 py-1.5 text-[11px] text-violet-700 bg-violet-50 border-b border-violet-100 sticky top-0">
                <b>{emailSearchResults.total}</b> risultati per &quot;{emailSearchResults.query}&quot;
              </div>
              {emailSearchResults.total === 0 ? (
                <div className="px-3 py-4 text-center text-xs text-slate-500">
                  Nessun risultato trovato in nessuna collezione.
                </div>
              ) : (
                <ul className="divide-y divide-slate-100">
                  {emailSearchResults.results.map((r, idx) => {
                    const typeColors = {
                      agency: "bg-violet-100 text-violet-800",
                      user: "bg-blue-100 text-blue-800",
                      client: "bg-emerald-100 text-emerald-800",
                      quote: "bg-amber-100 text-amber-800",
                      inbox_email: "bg-cyan-100 text-cyan-800",
                      outbox_email: "bg-pink-100 text-pink-800",
                    };
                    return (
                      <li key={`${r.type}-${idx}`} className="px-3 py-2 hover:bg-slate-50 text-xs">
                        <div className="flex items-start gap-2 flex-wrap">
                          <Badge className={`${typeColors[r.type] || "bg-slate-100 text-slate-800"} text-[10px] uppercase tracking-wide`}>
                            {r.label}
                          </Badge>
                          {r.agency?.name && (
                            <span className="text-[10px] text-slate-500">Agenzia: <b className="text-slate-700">{r.agency.name}</b></span>
                          )}
                        </div>
                        <div className="mt-1">
                          <span className="font-mono text-[11px] text-slate-700 break-all">
                            {r.email || r.from || r.to || "—"}
                          </span>
                        </div>
                        {r.name && <div className="text-[10px] text-slate-600">👤 {r.name}</div>}
                        {r.fiscal_code && <div className="text-[10px] text-slate-600 font-mono">🆔 CF: {r.fiscal_code}</div>}
                        {r.vat && <div className="text-[10px] text-slate-600 font-mono">🏛️ P.IVA: {r.vat}</div>}
                        {r.phone && <div className="text-[10px] text-slate-600">📞 {r.phone}</div>}
                        {r.subject && <div className="text-[10px] text-slate-500 truncate">📄 {r.subject}</div>}
                        {r.status && <div className="text-[10px] text-slate-400">stato: {r.status}</div>}
                        {r.sent_by && <div className="text-[10px] text-slate-400">inviata da: {r.sent_by}</div>}
                        {(r.received_at || r.sent_at) && (
                          <div className="text-[10px] text-slate-400">
                            {r.received_at ? "ricevuta" : "inviata"}: {new Date(r.received_at || r.sent_at).toLocaleString("it-IT")}
                          </div>
                        )}
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2 sm:gap-3">
        {[
          { lbl: "Totali", v: counts.total, c: "blue" },
          { lbl: "In attesa", v: counts.pending, c: "amber" },
          { lbl: "Attive", v: counts.active, c: "emerald" },
          { lbl: "Sospese", v: counts.suspended, c: "slate" },
          { lbl: "🔔 Pagamenti", v: counts.payment_pending, c: "rose" },
        ].map((k) => (
          <Card key={k.lbl} className={`border-${k.c}-200 bg-${k.c}-50/50`}>
            <CardContent className="pt-3 sm:pt-4 pb-2 sm:pb-3">
              <div className={`text-[9px] sm:text-[10px] uppercase tracking-wide text-${k.c}-700 font-semibold truncate`}>{k.lbl}</div>
              <div className="text-xl sm:text-2xl font-bold mt-0.5">{k.v}</div>
            </CardContent>
          </Card>
        ))}
      </div>


      {/* 🆕 Feb 2026 — Manutenzione DB: bonifica polizze legacy con link_type="reactivation" */}
      <Card className="border-amber-200">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm text-amber-800 flex items-center gap-2">🧹 Manutenzione database</CardTitle>
        </CardHeader>
        <CardContent className="pt-1 pb-3">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div className="text-[12px] text-slate-600 leading-snug max-w-xl">
              <b>Bonifica polizze riattivate legacy</b> — Azzera <code className="bg-slate-100 px-1 rounded text-[11px]">link_type=&quot;reactivation&quot;</code> sulle polizze create prima del fix Feb 2026, per evitare errori 422 al salvataggio dal dialog Modifica. Idempotente, sicuro da rilanciare.
            </div>
            <Button
              size="sm"
              variant="outline"
              data-testid="sa-backfill-reactivation"
              className="border-amber-400 text-amber-800 hover:bg-amber-50 whitespace-nowrap"
              onClick={async () => {
                if (!window.confirm("Eseguire la bonifica delle polizze legacy con link_type='reactivation'?")) return;
                try {
                  const { data } = await api.post("/superadmin/backfill-reactivation-link-type");
                  toast.success(`Bonifica completata: ${data.modified}/${data.found} polizze aggiornate`);
                } catch (e) {
                  toast.error(e?.response?.data?.detail || "Errore bonifica");
                }
              }}
            >
              🧹 Esegui bonifica riattivazioni
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* View mode toggle: Attive / Cestino */}
      <div className="flex items-center gap-2 border-b border-slate-200">
        <button
          type="button"
          data-testid="view-active"
          onClick={() => setViewMode("active")}
          className={`px-3 py-2 text-sm font-medium border-b-2 transition ${viewMode === "active" ? "border-violet-600 text-violet-700" : "border-transparent text-slate-500 hover:text-slate-700"}`}
        >
          <InboxIcon className="h-4 w-4 inline mr-1" /> Agenzie attive
        </button>
        <button
          type="button"
          data-testid="view-trash"
          onClick={() => setViewMode("trash")}
          className={`px-3 py-2 text-sm font-medium border-b-2 transition ${viewMode === "trash" ? "border-rose-600 text-rose-700" : "border-transparent text-slate-500 hover:text-slate-700"}`}
        >
          <Archive className="h-4 w-4 inline mr-1" /> Cestino {viewMode === "trash" && list.length > 0 && <span className="ml-1 text-[10px] bg-rose-100 text-rose-700 rounded-full px-1.5">{list.length}</span>}
        </button>
      </div>


      <Card>
        <CardHeader><CardTitle className="text-base">{viewMode === "trash" ? "🗑️ Cestino" : "Agenzie"} ({list.length})</CardTitle></CardHeader>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center py-10 text-slate-400"><Loader2 className="h-5 w-5 animate-spin mr-2" /> Caricamento…</div>
          ) : list.length === 0 ? (
            <div className="py-10 text-center text-slate-400 text-sm">
              {viewMode === "trash" ? "Il cestino è vuoto." : "Nessuna agenzia."}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-600">
                  <tr>
                    <th className="text-left px-3 sm:px-4 py-2.5">Agenzia</th>
                    <th className="text-left px-3 py-2.5 hidden md:table-cell">Email</th>
                    <th className="text-center px-3 py-2.5 min-w-[140px]">Stato</th>
                    <th className="text-center px-3 py-2.5 hidden lg:table-cell">Attivata il</th>
                    <th className="text-center px-3 py-2.5">{viewMode === "trash" ? "Cestinata il" : "Scadenza"}</th>
                    <th className="text-center px-3 py-2.5 hidden lg:table-cell">Dati</th>
                    <th className="text-center px-3 py-2.5 hidden lg:table-cell" title="Spazio archiviazione">💾 Storage</th>
                    <th className="text-right px-3 sm:px-4 py-2.5">Azioni</th>
                  </tr>
                </thead>
                <tbody>
                  {list.map((a) => {
                    const st = STATUS_STYLES[a.status] || STATUS_STYLES.pending;
                    // Calcolo client-side dei giorni rimanenti dalla data scadenza (sempre fresco)
                    let daysExp = null;
                    let expDate = null;
                    if (a.expires_at) {
                      try {
                        expDate = new Date(a.expires_at);
                        const today = new Date();
                        today.setHours(0, 0, 0, 0);
                        const exp0 = new Date(expDate);
                        exp0.setHours(0, 0, 0, 0);
                        daysExp = Math.round((exp0 - today) / 86400000);
                      } catch { /* ignore */ }
                    }
                    const expWarn = typeof daysExp === "number" && daysExp <= 20 && daysExp >= 0;
                    const expired = typeof daysExp === "number" && daysExp < 0;
                    return (
                      <tr key={a.id} className="border-t border-slate-100" data-testid={`agency-row-${a.id}`}>
                        <td className="px-3 sm:px-4 py-3">
                          <div className="font-medium truncate max-w-[140px] sm:max-w-none">{a.name}</div>
                          {a.is_main && <Badge variant="outline" className="mt-1 text-[10px]">Principale</Badge>}
                          {a.payment_pending_at && <Badge className="mt-1 text-[10px] bg-rose-100 text-rose-700 border-rose-300">💰 Pagamento ricevuto</Badge>}
                          <div className="text-[10px] text-slate-500 truncate md:hidden mt-0.5">{a.owner_email}</div>
                        </td>
                        <td className="px-3 py-3 text-xs text-slate-600 hidden md:table-cell truncate max-w-[200px]">{a.owner_email}</td>
                        <td className="px-3 py-3 text-center min-w-[140px]">
                          <span className={`inline-block px-2 py-0.5 text-[11px] rounded border whitespace-nowrap ${st.bg} ${st.text} ${st.border}`}>{st.label}</span>
                        </td>
                        <td className="px-3 py-3 text-center text-xs text-slate-600 hidden lg:table-cell" data-testid={`activated-${a.id}`}>
                          {a.activated_at ? new Date(a.activated_at).toLocaleDateString("it-IT") : <span className="text-slate-300">—</span>}
                        </td>
                        <td className="px-3 py-3 text-center text-xs" data-testid={`expiry-${a.id}`}>
                          {viewMode === "trash" ? (
                            a.deleted_at ? (
                              <span className="font-mono text-rose-700 text-[11px]">{new Date(a.deleted_at).toLocaleDateString("it-IT")}</span>
                            ) : <span className="text-slate-300">—</span>
                          ) : expDate ? (
                            <div className="flex flex-col items-center gap-0.5">
                              <span className="font-mono text-slate-700 text-[11px]">{expDate.toLocaleDateString("it-IT")}</span>
                              <span className={`text-[10px] font-semibold ${expired ? "text-rose-700" : expWarn ? "text-amber-700" : "text-emerald-600"}`}>
                                {expired ? `Scaduta da ${Math.abs(daysExp)} gg` : `${daysExp} gg`}
                              </span>
                            </div>
                          ) : <span className="text-slate-300">—</span>}
                        </td>
                        <td className="px-3 py-3 text-center text-[11px] text-slate-500 hidden lg:table-cell">
                          <div className="flex items-center justify-center gap-2.5">
                            <span title="Utenti"><UsersIcon className="inline h-3 w-3 mr-0.5" />{a.users_count || 0}</span>
                            <span title="Clienti"><Briefcase className="inline h-3 w-3 mr-0.5" />{a.clients_count || 0}</span>
                            <span title="Polizze"><FileText className="inline h-3 w-3 mr-0.5" />{a.policies_count || 0}</span>
                          </div>
                        </td>
                        {/* 🆕 Colonna Storage */}
                        <td className="px-3 py-3 text-center text-[11px] hidden lg:table-cell" data-testid={`storage-cell-${a.id}`}>
                          <button
                            type="button"
                            onClick={() => openResLimits(a)}
                            className="inline-flex flex-col items-center min-w-[100px] hover:opacity-80"
                            title="Clicca per modificare i limiti"
                          >
                            <span className="tabular-nums text-slate-700">
                              <b>{a.storage_used_mb ?? 0}</b> / {a.storage_limit_mb ?? 500} <span className="text-slate-400">MB</span>
                            </span>
                            <div className="mt-1 h-1.5 w-[100px] rounded-full bg-slate-200 overflow-hidden">
                              <div
                                className={`h-full ${
                                  (a.storage_used_percent ?? 0) >= 95
                                    ? "bg-red-500"
                                    : (a.storage_used_percent ?? 0) >= 80
                                    ? "bg-amber-500"
                                    : "bg-emerald-500"
                                }`}
                                style={{ width: `${Math.min(100, a.storage_used_percent ?? 0)}%` }}
                              />
                            </div>
                            <span className={`mt-0.5 text-[10px] ${
                              (a.storage_used_percent ?? 0) >= 95 ? "text-red-600 font-semibold"
                              : (a.storage_used_percent ?? 0) >= 80 ? "text-amber-700 font-medium"
                              : "text-slate-400"
                            }`}>
                              {(a.storage_used_percent ?? 0).toFixed(1)}%
                            </span>
                          </button>
                        </td>
                        <td className="px-3 sm:px-4 py-3 text-right">
                          <div className="inline-flex flex-wrap justify-end gap-1.5">
                            {viewMode === "trash" ? (
                              <>
                                <Button size="sm" data-testid={`restore-${a.id}`} className="bg-emerald-600 hover:bg-emerald-700 h-7 text-xs text-white" onClick={() => restoreAgency(a)}>
                                  <RotateCcw className="h-3.5 w-3.5 mr-1" /> Ripristina
                                </Button>
                                <Button size="sm" variant="outline" data-testid={`permanent-delete-${a.id}`} className="h-7 text-xs text-rose-700 border-rose-300 hover:bg-rose-50" onClick={() => openDelete(a)}>
                                  <Trash2 className="h-3.5 w-3.5 mr-1" /> Elimina definitivamente
                                </Button>
                              </>
                            ) : (
                              <>
                                {a.status === "pending" && (
                                  <>
                                    <Button size="sm" data-testid={`accept-${a.id}`} className="bg-sky-600 hover:bg-sky-700 h-7 text-xs" onClick={() => acceptRequest(a)}>
                                      <Mail className="h-3.5 w-3.5 mr-1" /> Accetta richiesta
                                    </Button>
                                    <Button size="sm" variant="outline" data-testid={`approve-${a.id}`} className="h-7 text-xs" onClick={() => { setApproveTarget(a); setTrialDays(30); }}>
                                      <CheckCircle2 className="h-3.5 w-3.5 mr-1" /> Attiva diretta
                                    </Button>
                                  </>
                                )}
                                {a.status === "accepted_awaiting_data" && (
                                  <Button size="sm" variant="outline" data-testid={`resend-${a.id}`} className="h-7 text-xs text-sky-700" onClick={() => resendOnboarding(a)}>
                                    <Send className="h-3.5 w-3.5 mr-1" /> Reinvia link
                                  </Button>
                                )}
                                {a.status === "data_submitted" && (
                                  <Button size="sm" data-testid={`review-${a.id}`} className="bg-violet-600 hover:bg-violet-700 h-7 text-xs" onClick={() => { setReviewTarget(a); setFinalizeDays(30); }}>
                                    <Eye className="h-3.5 w-3.5 mr-1" /> Vedi dati e attiva
                                  </Button>
                                )}
                                {/* 🆕 Modifica: ora abilitata anche per l'Agenzia Principale
                                    (permette di aggiornare email, nome, telefono, note) */}
                                <Button
                                  size="sm"
                                  variant="outline"
                                  data-testid={`edit-${a.id}`}
                                  className="h-7 text-xs"
                                  onClick={() => openEdit(a)}
                                  title={a.is_main ? "Modifica dati Agenzia Principale (nome, email, ecc.)" : "Modifica dati agenzia"}
                                >
                                  <Pencil className="h-3.5 w-3.5 mr-1" /> Modifica
                                </Button>
                                {/* 🆕 Feb 2026 — Dettaglio Accessi (SuperAdmin login monitor) */}
                                <Button
                                  size="sm"
                                  variant="outline"
                                  data-testid={`access-log-${a.id}`}
                                  className="h-7 text-xs text-cyan-700 border-cyan-300 hover:bg-cyan-50"
                                  onClick={() => openAccessLog(a)}
                                  title="Vedi utenti online e ultimi 20 accessi"
                                >
                                  <Eye className="h-3.5 w-3.5 mr-1" /> Accessi
                                </Button>
                                {a.status !== "pending" && !a.is_main && (
                                  <Button size="sm" variant="outline" data-testid={`extend-${a.id}`} className="h-7 text-xs" onClick={() => openExtend(a)}>
                                    <CalendarPlus className="h-3.5 w-3.5 mr-1" /> Estendi
                                  </Button>
                                )}
                                {!a.is_main && (
                                  <Button size="sm" variant="outline" data-testid={`change-slug-${a.id}`} className="h-7 text-xs text-indigo-700 border-indigo-300 hover:bg-indigo-50" onClick={() => changeAgencySlug(a)} title={`Slug attuale: /${a.slug}`}>
                                    <Link2 className="h-3.5 w-3.5 mr-1" /> Slug
                                  </Button>
                                )}
                                {a.status === "active" && !a.is_main && (
                                  <Button size="sm" variant="outline" data-testid={`suspend-${a.id}`} className="h-7 text-xs text-rose-600 hover:bg-rose-50" onClick={() => doSuspend(a)}>
                                    <XCircle className="h-3.5 w-3.5 mr-1" /> Sospendi
                                  </Button>
                                )}
                                {a.status === "suspended" && !a.is_main && (
                                  <Button size="sm" variant="outline" data-testid={`reactivate-${a.id}`} className="h-7 text-xs text-emerald-700 border-emerald-300 hover:bg-emerald-50" onClick={() => doReactivate(a)}>
                                    <CheckCircle2 className="h-3.5 w-3.5 mr-1" /> Riattiva
                                  </Button>
                                )}
                                {!a.is_main && (
                                  <Button size="sm" variant="outline" data-testid={`poll-inbox-${a.id}`} className="h-7 text-xs text-blue-700 border-blue-300 hover:bg-blue-50" onClick={() => pollForAgency(a)} title="Forza polling IMAP e mostra nuove email per questa agenzia">
                                    <InboxIcon className="h-3.5 w-3.5 mr-1" /> Polling
                                  </Button>
                                )}
                                {!a.is_main && (
                                  <Button size="sm" variant="outline" data-testid={`ai-limits-${a.id}`} className="h-7 text-xs text-violet-700 border-violet-300 hover:bg-violet-50" onClick={() => openAiLimits(a)}>
                                    <Brain className="h-3.5 w-3.5 mr-1" /> Limiti IA
                                  </Button>
                                )}
                                {!a.is_main && (
                                  <Button size="sm" variant="outline" data-testid={`res-limits-${a.id}`} className="h-7 text-xs text-indigo-700 border-indigo-300 hover:bg-indigo-50" onClick={() => openResLimits(a)}>
                                    <SlidersHorizontal className="h-3.5 w-3.5 mr-1" /> Limiti risorse
                                  </Button>
                                )}
                                {!a.is_main && (
                                  <Button size="sm" variant="outline" data-testid={`delete-${a.id}`} className="h-7 text-xs text-rose-700 border-rose-300 hover:bg-rose-50" onClick={() => openDelete(a)}>
                                    <Trash2 className="h-3.5 w-3.5 mr-1" /> Cestina
                                  </Button>
                                )}
                              </>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Dialog approve */}
      <Dialog open={!!approveTarget} onOpenChange={(o) => !o && setApproveTarget(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle>Approva {approveTarget?.name}</DialogTitle></DialogHeader>
          <p className="text-sm text-slate-600">Imposta i giorni di prova iniziali.</p>
          <Input type="number" min="1" max="365" value={trialDays} onChange={(e) => setTrialDays(e.target.value)} data-testid="approve-days" />
          <DialogFooter>
            <Button variant="outline" onClick={() => setApproveTarget(null)}>Annulla</Button>
            <Button onClick={doApprove} disabled={busy} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="approve-confirm">
              {busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}Approva
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>


      {/* Dialog Anteprima Notifica Email — sospensione/riattivazione agenzia */}
      <Dialog open={!!notifyPreview} onOpenChange={(o) => { if (!o && !notifyPreview?.busy) setNotifyPreview(null); }}>
        <DialogContent className="max-w-2xl" data-testid="notify-preview-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 flex-wrap">
              {notifyPreview?.action === "suspend" ? (
                <><XCircle className="h-5 w-5 text-rose-600" /> Sospensione agenzia</>
              ) : (
                <><CheckCircle2 className="h-5 w-5 text-emerald-600" /> Riattivazione agenzia</>
              )}
              {notifyPreview?.agency?.name && (
                <span className="text-sm font-normal bg-slate-100 px-2 py-0.5 rounded text-slate-700">{notifyPreview.agency.name}</span>
              )}
            </DialogTitle>
          </DialogHeader>
          {notifyPreview && (
            <div className="space-y-3">
              <div className="rounded-md border border-amber-200 bg-amber-50 p-3 text-xs text-amber-900">
                {notifyPreview.action === "suspend" ? (
                  <><strong>Conferma sospensione.</strong> Tutti gli utenti dell&apos;agenzia verranno disattivati e non potranno più accedere. Decidi se inviare anche l&apos;email di notifica al titolare.</>
                ) : (
                  <><strong>Conferma riattivazione.</strong> Tutti gli utenti dell&apos;agenzia verranno riattivati. Decidi se inviare anche l&apos;email di notifica al titolare.</>
                )}
              </div>
              <div className="border border-slate-200 rounded-md overflow-hidden">
                <div className="bg-slate-50 border-b border-slate-200 px-3 py-2 text-xs space-y-0.5">
                  <div><span className="text-slate-500">Destinatario:</span> <strong className="font-mono">{notifyPreview.target_email || "(nessuna email)"}</strong></div>
                  <div><span className="text-slate-500">Oggetto:</span> <strong>{notifyPreview.subject}</strong></div>
                </div>
                <div
                  className="px-4 py-3 text-sm max-h-72 overflow-y-auto bg-white"
                  data-testid="notify-preview-body"
                  dangerouslySetInnerHTML={{ __html: notifyPreview.html || "" }}
                />
              </div>
            </div>
          )}
          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button
              variant="ghost"
              onClick={() => setNotifyPreview(null)}
              disabled={notifyPreview?.busy}
              data-testid="notify-preview-cancel"
            >
              Annulla
            </Button>
            <Button
              variant="outline"
              onClick={() => confirmNotification(false)}
              disabled={notifyPreview?.busy}
              data-testid="notify-preview-no-email"
              title="Procedi senza inviare email al cliente"
              className="border-slate-300"
            >
              {notifyPreview?.busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
              Conferma <span className="font-bold mx-1">SENZA</span> email
            </Button>
            <Button
              onClick={() => confirmNotification(true)}
              disabled={notifyPreview?.busy || !notifyPreview?.target_email}
              data-testid="notify-preview-send-email"
              className={notifyPreview?.action === "suspend" ? "bg-rose-600 hover:bg-rose-700 text-white" : "bg-emerald-600 hover:bg-emerald-700 text-white"}
              title={!notifyPreview?.target_email ? "Nessuna email destinatario" : "Procedi e invia email al cliente"}
            >
              {notifyPreview?.busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Send className="h-4 w-4 mr-2" />}
              Conferma e invia email
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog extend */}
      <Dialog open={!!extendTarget} onOpenChange={(o) => !o && setExtendTarget(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle>Estendi {extendTarget?.name}</DialogTitle></DialogHeader>
          {extendTarget?.payment_pending_at && (
            <div className="rounded-md border border-emerald-300 bg-emerald-50 p-2.5 text-xs">
              <AlertCircle className="inline h-3.5 w-3.5 mr-1 text-emerald-700" />
              Pagamento confermato dal cliente. {extendTarget.payment_note && <span className="block mt-1 italic">Nota: {extendTarget.payment_note}</span>}
            </div>
          )}
          {extendTarget && (
            <div className="rounded-md bg-slate-50 border border-slate-200 p-2.5 text-xs text-slate-600 space-y-0.5">
              <div>Scadenza attuale: <strong>{extendTarget.expires_at ? new Date(extendTarget.expires_at).toLocaleDateString("it-IT") : "—"}</strong></div>
              <div>Periodo iniziale: <strong>{extendTarget.trial_days || (extendTarget.activated_at && extendTarget.expires_at ? Math.round((new Date(extendTarget.expires_at) - new Date(extendTarget.activated_at)) / 86400000) : 365)} giorni</strong> <span className="text-slate-400">(usato come default)</span></div>
            </div>
          )}
          <div>
            <label className="text-xs font-medium text-slate-700 mb-1 block">Nuova data di scadenza</label>
            <DatePicker min={tomorrowISO()}
              value={extendUntil}
              onChange={(e) => setExtendUntil(e.target.value)}
              data-testid="extend-until"
              className="font-mono" />
            <p className="text-[11px] text-slate-500 mt-1">📅 Seleziona la data fino alla quale prolungare l&apos;abbonamento.</p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setExtendTarget(null)}>Annulla</Button>
            <Button onClick={doExtend} disabled={busy || !extendUntil} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="extend-confirm">
              {busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}Estendi
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog create agency */}
      <Dialog open={createOpen} onOpenChange={(o) => !o && setCreateOpen(false)}>
        <DialogContent className="max-w-md" data-testid="create-agency-dialog">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><Plus className="h-5 w-5 text-violet-600" /> Crea nuova agenzia</DialogTitle></DialogHeader>
          <p className="text-xs text-slate-500 -mt-2">Crea direttamente un&apos;agenzia. Se &quot;Attiva subito&quot; è abilitato, l&apos;owner potrà accedere immediatamente.</p>
          <div className="space-y-2.5">
            <div>
              <label className="text-xs font-medium block mb-1">Nome agenzia <span className="text-red-500">*</span></label>
              <Input data-testid="create-agency-name" value={createForm.agency_name} onChange={(e) => setCreateForm({ ...createForm, agency_name: e.target.value })} placeholder="Es. Agenzia Rossi" />
            </div>
            <div>
              <label className="text-xs font-medium block mb-1">Nome owner <span className="text-red-500">*</span></label>
              <Input data-testid="create-owner-name" value={createForm.owner_name} onChange={(e) => setCreateForm({ ...createForm, owner_name: e.target.value })} placeholder="Mario Rossi" />
            </div>
            <div>
              <label className="text-xs font-medium block mb-1">Email owner <span className="text-red-500">*</span></label>
              <Input data-testid="create-email" type="email" value={createForm.owner_email} onChange={(e) => setCreateForm({ ...createForm, owner_email: e.target.value.toLowerCase() })} placeholder="mario@agenzia.it" />
            </div>
            <div>
              <label className="text-xs font-medium block mb-1">Password iniziale <span className="text-red-500">*</span></label>
              <Input data-testid="create-password" type="text" value={createForm.owner_password} onChange={(e) => setCreateForm({ ...createForm, owner_password: e.target.value })} placeholder="Min. 8 caratteri" />
              <p className="text-[10px] text-slate-400 mt-0.5">Verrà inviata via email all&apos;owner.</p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              <div>
                <label className="text-xs font-medium block mb-1">Cellulare</label>
                <Input data-testid="create-phone" value={createForm.phone} onChange={(e) => setCreateForm({ ...createForm, phone: e.target.value })} placeholder="3331234567" />
              </div>
              <div>
                <label className="text-xs font-medium block mb-1">Periodo (giorni)</label>
                <Input data-testid="create-trial-days" type="number" min="1" max="3650" value={createForm.trial_days} onChange={(e) => setCreateForm({ ...createForm, trial_days: e.target.value })} />
              </div>
            </div>
            <label className="flex items-center gap-2 text-xs cursor-pointer">
              <input
                type="checkbox"
                data-testid="create-auto-activate"
                checked={createForm.auto_activate}
                onChange={(e) => setCreateForm({ ...createForm, auto_activate: e.target.checked })}
                className="h-4 w-4"
              />
              <span>Attiva subito l&apos;agenzia (l&apos;owner può accedere immediatamente)</span>
            </label>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateOpen(false)}>Annulla</Button>
            <Button onClick={doCreate} disabled={busy} className="bg-violet-600 hover:bg-violet-700 text-white" data-testid="create-confirm">
              {busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}Crea agenzia
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {/* Dialog edit agency */}
      <Dialog open={!!editTarget} onOpenChange={(o) => !o && setEditTarget(null)}>
        <DialogContent className="max-w-3xl w-[calc(100vw-1rem)] max-h-[90vh] overflow-y-auto p-4 sm:p-6" data-testid="edit-agency-dialog">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><Pencil className="h-5 w-5 text-blue-600" /> Modifica {editTarget?.name}{editTarget?.is_main && <span className="ml-1 text-[10px] font-normal px-1.5 py-0.5 rounded bg-blue-100 text-blue-700 border border-blue-200">🏛️ Agenzia Principale</span>}</DialogTitle></DialogHeader>
          <p className="text-xs text-slate-500 -mt-2">{editTarget?.is_main ? "Aggiorna nome, email e dati anagrafici dell'Agenzia Principale." : "Aggiorna i dati anagrafici dell'agenzia e dell'owner."}</p>
          <div className="space-y-2.5">
            <div>
              <label className="text-xs font-medium block mb-1">Nome agenzia <span className="text-red-500">*</span></label>
              <Input data-testid="edit-agency-name" value={editForm.agency_name} onChange={(e) => setEditForm({ ...editForm, agency_name: e.target.value })} />
            </div>
            <div>
              <label className="text-xs font-medium block mb-1">Nome owner</label>
              <Input data-testid="edit-owner-name" value={editForm.owner_name} onChange={(e) => setEditForm({ ...editForm, owner_name: e.target.value })} />
            </div>
            <div>
              <label className="text-xs font-medium block mb-1">Email owner <span className="text-red-500">*</span></label>
              <Input data-testid="edit-email" type="email" value={editForm.owner_email} onChange={(e) => setEditForm({ ...editForm, owner_email: e.target.value.toLowerCase() })} />
            </div>
            <div>
              <label className="text-xs font-medium block mb-1">Nuova password <span className="text-slate-400">(lascia vuoto per non cambiare)</span></label>
              <Input data-testid="edit-password" type="text" value={editForm.owner_password} onChange={(e) => setEditForm({ ...editForm, owner_password: e.target.value })} placeholder="Min. 8 caratteri" />
            </div>
            <div>
              <label className="text-xs font-medium block mb-1">Cellulare</label>
              <Input data-testid="edit-phone" value={editForm.phone} onChange={(e) => setEditForm({ ...editForm, phone: e.target.value })} />
            </div>
            {/* 🆕 Date di attivazione/scadenza: irrilevanti per l'Agenzia Principale (no abbonamento) */}
            {!editTarget?.is_main && (
              <>
                <div>
                  <label className="text-xs font-medium block mb-1">📅 Data di attivazione</label>
                  <DatePicker data-testid="edit-activated-at" value={editForm.activated_at}
                    onChange={(e) => setEditForm({ ...editForm, activated_at: e.target.value })}
                    className="font-mono" />
                  <p className="text-[10px] text-slate-400 mt-0.5">Modifica retroattivamente la data di attivazione (utile per allineare con fatturazione).</p>
                </div>
                <div>
                  <label className="text-xs font-medium block mb-1">⏰ Data di scadenza abbonamento</label>
                  <DatePicker data-testid="edit-expires-at" value={editForm.expires_at}
                    onChange={(e) => setEditForm({ ...editForm, expires_at: e.target.value })}
                    className="font-mono" />
                  <p className="text-[10px] text-slate-400 mt-0.5">Modifica direttamente la scadenza dell&apos;abbonamento. Se la nuova data è futura, l&apos;agenzia viene riattivata automaticamente.</p>
                </div>
              </>
            )}
            <div>
              <label className="text-xs font-medium block mb-1">Note</label>
              <Input data-testid="edit-notes" value={editForm.notes} onChange={(e) => setEditForm({ ...editForm, notes: e.target.value })} />
            </div>
            <div className="rounded-md border border-amber-200 bg-amber-50/50 p-2.5">
              <label className="text-xs font-medium block mb-1 text-amber-900">
                Email destinatario OTP (impostazioni sensibili)
              </label>
              <Input
                data-testid="edit-otp-recipient-email"
                type="email"
                placeholder={`Vuoto = usa owner (${editForm.owner_email || "—"})`}
                value={editForm.otp_recipient_email || ""}
                onChange={(e) => setEditForm({ ...editForm, otp_recipient_email: e.target.value })}
              />
              <p className="text-[10px] text-amber-700 mt-1">
                Se valorizzato, i codici OTP per modificare Email SMTP e Area Pubblica vengono inviati a
                questo indirizzo (es. <code>security@agenzia.it</code>) invece che all&apos;owner.
                Lascia vuoto per usare l&apos;email dell&apos;owner.
              </p>
            </div>
            <div className="rounded-md border border-blue-200 bg-blue-50/50 p-2.5 flex items-start gap-2">
              <input
                type="checkbox"
                id="edit-is-demo"
                data-testid="edit-is-demo"
                checked={!!editForm.is_demo}
                onChange={(e) => setEditForm({ ...editForm, is_demo: e.target.checked })}
                className="mt-0.5"
              />
              <label htmlFor="edit-is-demo" className="text-xs text-blue-900 cursor-pointer">
                <strong>Agenzia Demo</strong>
                <p className="text-[10px] text-blue-700 mt-0.5 font-normal">
                  Se attivo, all&apos;apertura dell&apos;app appare un popup che invita l&apos;utente ad attivare la versione definitiva.
                </p>
              </label>
            </div>
          </div>

          {/* 🆕 30/07/2026 — Accessi Demo Multipli (solo se agenzia è demo) */}
          {editForm.is_demo && editTarget?.id && (
            <div className="border-t border-slate-200 pt-4 mt-2" data-testid="demo-accesses-section">
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-sm font-semibold text-slate-800 flex items-center gap-1.5">👥 Accessi Demo <span className="text-xs font-normal text-slate-400">({demoAccesses.length})</span></h3>
                <Button
                  size="sm"
                  data-testid="new-demo-access-btn"
                  onClick={() => setDemoAccessDialog({ form: { email: "", name: "", access_activation: new Date().toISOString().slice(0,10), access_expiration: new Date(Date.now()+14*24*3600*1000).toISOString().slice(0,10), send_email: true } })}
                  className="bg-cyan-600 hover:bg-cyan-700 text-white h-7 text-xs"
                >
                  + Nuovo accesso demo
                </Button>
              </div>
              {demoAccesses.length === 0 ? (
                <p className="text-xs text-slate-400 italic">Nessun accesso demo. Clicca su &quot;Nuovo accesso demo&quot; per crearne uno.</p>
              ) : (
                <div className="border border-slate-200 rounded-md overflow-hidden max-h-52 overflow-y-auto">
                  <table className="w-full text-xs">
                    <thead className="bg-slate-50 text-slate-600">
                      <tr>
                        <th className="text-left px-2 py-1.5">Email</th>
                        <th className="text-left px-2 py-1.5">Attivazione</th>
                        <th className="text-left px-2 py-1.5">Scadenza</th>
                        <th className="text-left px-2 py-1.5">Stato</th>
                        <th className="text-right px-2 py-1.5">Azioni</th>
                      </tr>
                    </thead>
                    <tbody>
                      {demoAccesses.map((u) => {
                        const now = new Date();
                        const act = u.access_activation ? new Date(u.access_activation) : null;
                        const exp = u.access_expiration ? new Date(u.access_expiration) : null;
                        let status = "Attivo", statusClass = "bg-emerald-100 text-emerald-700";
                        if (exp && now > exp) { status = "Scaduto"; statusClass = "bg-red-100 text-red-700"; }
                        else if (act && now < act) { status = "In attesa"; statusClass = "bg-amber-100 text-amber-700"; }
                        return (
                          <tr key={u.id} data-testid={`demo-access-row-${u.id}`} className="border-t border-slate-100">
                            <td className="px-2 py-1.5">
                              <div className="font-medium text-slate-800">{u.email}</div>
                              <div className="text-[10px] text-slate-400">{u.name}</div>
                            </td>
                            <td className="px-2 py-1.5">{act ? act.toLocaleDateString("it-IT") : "—"}</td>
                            <td className="px-2 py-1.5">{exp ? exp.toLocaleDateString("it-IT") : "—"}</td>
                            <td className="px-2 py-1.5">
                              <span className={`inline-block px-1.5 py-0.5 rounded text-[10px] font-semibold ${statusClass}`}>{status}</span>
                            </td>
                            <td className="px-2 py-1.5 text-right">
                              <button
                                type="button"
                                data-testid={`revoke-demo-access-${u.id}`}
                                onClick={async () => {
                                  if (!window.confirm(`Revocare l'accesso demo di ${u.email}?`)) return;
                                  try {
                                    await api.delete(`/superadmin/demo-accesses/${u.id}`);
                                    setDemoAccesses((prev) => prev.filter((x) => x.id !== u.id));
                                    toast.success("Accesso demo revocato");
                                  } catch (e) { toast.error("Revoca fallita: " + (e?.response?.data?.detail || e.message)); }
                                }}
                                className="text-red-600 hover:underline text-[11px]"
                              >Revoca</button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
              {lastDemoTempPwd && (
                <div className="mt-2 p-2 bg-amber-50 border border-amber-200 rounded text-xs" data-testid="demo-temp-password-shown">
                  <strong>⚠️ Password temporanea:</strong> <code className="bg-white px-2 py-0.5 rounded">{lastDemoTempPwd}</code>
                  <p className="text-[10px] text-amber-700 mt-1">Copia subito questa password — non sarà più mostrata dopo la chiusura del dialog.</p>
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditTarget(null)}>Annulla</Button>
            <Button onClick={doEdit} disabled={busy} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="edit-confirm">
              {busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}Salva modifiche
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {/* 🆕 30/07/2026 — Dialog Nuovo Accesso Demo */}
      <Dialog open={!!demoAccessDialog} onOpenChange={(o) => { if (!o) setDemoAccessDialog(null); }}>
        <DialogContent className="max-w-md" data-testid="new-demo-access-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <span className="text-cyan-600">👥</span> Nuovo accesso demo
            </DialogTitle>
          </DialogHeader>
          {demoAccessDialog && (
            <div className="space-y-3">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                <div>
                  <Label>Nome *</Label>
                  <Input
                    data-testid="demo-access-name"
                    value={demoAccessDialog.form.name}
                    onChange={(e) => setDemoAccessDialog({ form: { ...demoAccessDialog.form, name: e.target.value } })}
                    placeholder="Es. Mario Rossi"
                  />
                </div>
                <div>
                  <Label>Email *</Label>
                  <Input
                    type="email"
                    data-testid="demo-access-email"
                    value={demoAccessDialog.form.email}
                    onChange={(e) => setDemoAccessDialog({ form: { ...demoAccessDialog.form, email: e.target.value } })}
                    placeholder="mario@example.it"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label>Data attivazione</Label>
                  <Input
                    type="date"
                    data-testid="demo-access-activation"
                    value={demoAccessDialog.form.access_activation}
                    onChange={(e) => setDemoAccessDialog({ form: { ...demoAccessDialog.form, access_activation: e.target.value } })}
                  />
                </div>
                <div>
                  <Label>Data scadenza *</Label>
                  <Input
                    type="date"
                    data-testid="demo-access-expiration"
                    value={demoAccessDialog.form.access_expiration}
                    onChange={(e) => setDemoAccessDialog({ form: { ...demoAccessDialog.form, access_expiration: e.target.value } })}
                  />
                </div>
              </div>
              <div className="flex items-start gap-2 p-2 bg-cyan-50 border border-cyan-200 rounded">
                <input
                  type="checkbox"
                  id="demo-access-send-email"
                  data-testid="demo-access-send-email"
                  checked={demoAccessDialog.form.send_email}
                  onChange={(e) => setDemoAccessDialog({ form: { ...demoAccessDialog.form, send_email: e.target.checked } })}
                  className="mt-0.5"
                />
                <label htmlFor="demo-access-send-email" className="text-xs text-cyan-900 cursor-pointer">
                  <strong>Invia credenziali via email</strong>
                  <p className="text-[10px] text-cyan-700 mt-0.5 font-normal">
                    Se disattivo, la password temporanea verrà mostrata solo qui (una tantum) — copia subito.
                  </p>
                </label>
              </div>
              <p className="text-[10px] text-slate-500 -mt-1">
                Verrà generata una password univoca da 16 caratteri, che scade automaticamente alla data indicata.
              </p>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setDemoAccessDialog(null)} disabled={demoAccessBusy}>Annulla</Button>
            <Button
              data-testid="demo-access-create"
              onClick={async () => {
                const f = demoAccessDialog?.form;
                if (!f?.email?.trim() || !f?.name?.trim() || !f?.access_expiration) {
                  toast.error("Compila Nome, Email e Data scadenza");
                  return;
                }
                setDemoAccessBusy(true);
                try {
                  const { data } = await api.post(`/superadmin/agencies/${editTarget.id}/demo-accesses`, f);
                  toast.success(data.email_sent ? "✉️ Accesso creato e credenziali inviate via email" : "Accesso creato — copia la password temporanea!");
                  setDemoAccesses((prev) => [data, ...prev]);
                  if (data.temp_password) setLastDemoTempPwd(data.temp_password);
                  setDemoAccessDialog(null);
                } catch (e) {
                  toast.error("Creazione fallita: " + (e?.response?.data?.detail || e.message));
                } finally { setDemoAccessBusy(false); }
              }}
              disabled={demoAccessBusy}
              className="bg-cyan-600 hover:bg-cyan-700 text-white"
            >
              {demoAccessBusy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
              Crea accesso
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {/* Dialog SA Sensitive OTP (richiesta per cambio email owner / reset password) */}
      <Dialog open={saOtpOpen} onOpenChange={(o) => { if (!o) { setSaOtpOpen(false); setSaOtpStep("idle"); setSaPendingPayload(null); setSaOtpCode(""); } }}>
        <DialogContent className="max-w-sm" data-testid="sa-otp-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-amber-800">
              <AlertCircle className="h-5 w-5 text-amber-600" />
              Verifica Super-Admin
            </DialogTitle>
          </DialogHeader>
          <p className="text-sm text-slate-600 -mt-1">
            Stai modificando l&apos;email del proprietario o la password admin. Per sicurezza serve la verifica OTP.
          </p>
          {saOtpStep === "sent" && saOtpRecipient && (
            <div className="rounded-md border border-emerald-200 bg-emerald-50 p-2.5 text-xs text-emerald-800">
              Codice inviato a <b>{saOtpRecipient}</b>. Scade in 15 minuti.
            </div>
          )}
          <div>
            <label className="text-xs font-medium block mb-1">Codice OTP (6 cifre)</label>
            <Input
              data-testid="sa-otp-code"
              value={saOtpCode}
              onChange={(e) => setSaOtpCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
              placeholder="000000"
              className="text-center tracking-[0.4em] font-mono text-lg"
              maxLength={6}
              inputMode="numeric"
              autoFocus
            />
          </div>
          <DialogFooter className="flex-wrap gap-2">
            <Button
              variant="outline"
              onClick={requestSaOtp}
              disabled={saOtpBusy}
              data-testid="sa-otp-resend"
              className="text-xs"
            >
              {saOtpStep === "sent" ? "Reinvia codice" : "Invia codice"}
            </Button>
            <Button
              onClick={verifySaOtpAndRetry}
              disabled={saOtpBusy || saOtpCode.length !== 6}
              data-testid="sa-otp-verify"
              className="bg-emerald-600 hover:bg-emerald-700 text-white"
            >
              {saOtpBusy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}Verifica e salva
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {/* Dialog AI usage limits */}
      <Dialog open={!!aiLimitsTarget} onOpenChange={(o) => !o && setAiLimitsTarget(null)}>
        <DialogContent className="max-w-md" data-testid="ai-limits-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Brain className="h-5 w-5 text-violet-600" />
              Limiti IA — {aiLimitsTarget?.name}
            </DialogTitle>
          </DialogHeader>
          <p className="text-xs text-slate-500 -mt-2">
            Imposta il numero massimo di richieste IA (OCR libretto, estrazione documenti, riepiloghi automatici, ecc.) che l&apos;agenzia può effettuare. Il limite è condiviso fra tutti gli utenti dell&apos;agenzia. <strong>0 = illimitato</strong>.
          </p>
          {aiLimitsLoading ? (
            <div className="flex items-center justify-center py-6 text-slate-500">
              <Loader2 className="h-5 w-5 animate-spin" />
            </div>
          ) : (
            <div className="space-y-3">
              <div className="rounded-md border border-violet-200 bg-violet-50 p-2.5 text-xs text-violet-900">
                <div className="font-semibold mb-1">Consumo attuale</div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                  <div><span className="text-violet-600">Oggi:</span> <b>{aiLimitsUsage.daily}</b></div>
                  <div><span className="text-violet-600">Mese:</span> <b>{aiLimitsUsage.monthly}</b></div>
                  <div><span className="text-violet-600">Anno:</span> <b>{aiLimitsUsage.annual}</b></div>
                </div>
              </div>
              {aiUsageHistory.length > 0 && (
                <div className="rounded-md border border-slate-200 bg-white p-2.5">
                  <div className="flex items-baseline justify-between mb-1">
                    <span className="text-xs font-semibold text-slate-700">Consumo IA — ultimi 30 giorni</span>
                    <span className="text-[10px] text-slate-400 tabular-nums">
                      tot {aiUsageHistory.reduce((acc, d) => acc + d.count, 0)}
                    </span>
                  </div>
                  <div style={{ width: "100%", height: 120 }}>
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={aiUsageHistory} margin={{ top: 5, right: 4, left: -20, bottom: 0 }}>
                        <CartesianGrid stroke="#f1f5f9" vertical={false} />
                        <XAxis dataKey="label" interval={4} tick={{ fontSize: 9, fill: "#64748b" }} axisLine={false} tickLine={false} />
                        <YAxis allowDecimals={false} tick={{ fontSize: 9, fill: "#64748b" }} axisLine={false} tickLine={false} width={28} />
                        <Tooltip
                          contentStyle={{ background: "#0A192F", border: "none", borderRadius: 6, color: "white", fontSize: 11 }}
                          labelStyle={{ color: "#cbd5e1" }}
                          labelFormatter={(l, items) => items?.[0]?.payload?.date || l}
                          formatter={(v) => [v, "chiamate IA"]}
                        />
                        <Bar dataKey="count" fill="#8b5cf6" radius={[3, 3, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              )}
              <div>
                <label className="text-xs font-medium block mb-1">Limite giornaliero</label>
                <Input
                  data-testid="ai-limits-daily"
                  type="number"
                  min="0"
                  value={aiLimitsForm.daily}
                  onChange={(e) => setAiLimitsForm({ ...aiLimitsForm, daily: e.target.value })}
                />
              </div>
              <div>
                <label className="text-xs font-medium block mb-1">Limite mensile</label>
                <Input
                  data-testid="ai-limits-monthly"
                  type="number"
                  min="0"
                  value={aiLimitsForm.monthly}
                  onChange={(e) => setAiLimitsForm({ ...aiLimitsForm, monthly: e.target.value })}
                />
              </div>
              <div>
                <label className="text-xs font-medium block mb-1">Limite annuale</label>
                <Input
                  data-testid="ai-limits-annual"
                  type="number"
                  min="0"
                  value={aiLimitsForm.annual}
                  onChange={(e) => setAiLimitsForm({ ...aiLimitsForm, annual: e.target.value })}
                />
              </div>
              <p className="text-[10px] text-slate-400">
                Default piattaforma: 50 al giorno / 1000 al mese / 10.000 all&apos;anno.
              </p>

              {/* 🆕 Sezione Storage: limite MB per l'agenzia */}
              <div className="mt-3 pt-3 border-t border-slate-200 space-y-2">
                <div className="flex items-center justify-between">
                  <label className="text-xs font-semibold uppercase tracking-wider text-slate-700">💾 Limite spazio archiviazione (MB)</label>
                  {storageUsageInfo && (
                    <span className="text-[10px] text-slate-600 tabular-nums">
                      Usato ora: <strong>{storageUsageInfo.used_mb} MB</strong> ({storageUsageInfo.used_percent}%)
                    </span>
                  )}
                </div>
                <Input
                  data-testid="storage-limit-mb"
                  type="number"
                  min="1"
                  step="50"
                  value={storageLimitMb}
                  onChange={(e) => setStorageLimitMb(e.target.value)}
                  placeholder="500"
                />
                <p className="text-[10px] text-slate-400">
                  Limite di spazio per allegati (polizze, documenti cliente, preventivi, email).
                  Default piattaforma: 500 MB.
                </p>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setAiLimitsTarget(null)}>Annulla</Button>
            <Button
              onClick={doSaveAiLimits}
              disabled={busy || aiLimitsLoading}
              className="bg-violet-600 hover:bg-violet-700 text-white"
              data-testid="ai-limits-save"
            >
              {busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}Salva limiti
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {/* Dialog Resource Limits (clienti / polizze) */}
      <Dialog open={!!resLimitsTarget} onOpenChange={(o) => !o && (setResLimitsTarget(null), setResLimitsData(null))}>
        <DialogContent className="max-w-lg" data-testid="res-limits-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <SlidersHorizontal className="h-5 w-5 text-indigo-600" />
              Limiti risorse — {resLimitsTarget?.name}
            </DialogTitle>
          </DialogHeader>
          <p className="text-xs text-slate-500 -mt-2">
            Imposta il numero massimo di <b>clienti</b> e <b>polizze</b> che questa agenzia può creare.
            Lascia vuoto (o 0) per <b>illimitato</b>. I limiti vengono ereditati dal piano sottoscritto;
            qui puoi sovrascriverli per la singola agenzia.
          </p>
          {resLimitsLoading || !resLimitsData ? (
            <div className="flex items-center justify-center py-6 text-slate-500">
              <Loader2 className="h-5 w-5 animate-spin" />
            </div>
          ) : (
            <div className="space-y-3">
              <div className="rounded-md border border-indigo-200 bg-indigo-50 p-3 text-xs">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <div className="text-indigo-700 font-semibold mb-1">Clienti</div>
                    <div className="tabular-nums text-slate-800">
                      <b>{resLimitsData.used.clients}</b> / {resLimitsData.limits.max_clients ?? "∞"}
                    </div>
                    <div className="mt-1 h-1.5 rounded-full bg-white overflow-hidden">
                      <div
                        className="h-full bg-indigo-500"
                        style={{
                          width: resLimitsData.limits.max_clients
                            ? `${Math.min(100, (resLimitsData.used.clients / resLimitsData.limits.max_clients) * 100)}%`
                            : "100%",
                          opacity: resLimitsData.limits.max_clients ? 1 : 0.3,
                        }}
                      />
                    </div>
                  </div>
                  <div>
                    <div className="text-indigo-700 font-semibold mb-1">Polizze</div>
                    <div className="tabular-nums text-slate-800">
                      <b>{resLimitsData.used.policies}</b> / {resLimitsData.limits.max_policies ?? "∞"}
                    </div>
                    <div className="mt-1 h-1.5 rounded-full bg-white overflow-hidden">
                      <div
                        className="h-full bg-indigo-500"
                        style={{
                          width: resLimitsData.limits.max_policies
                            ? `${Math.min(100, (resLimitsData.used.policies / resLimitsData.limits.max_policies) * 100)}%`
                            : "100%",
                          opacity: resLimitsData.limits.max_policies ? 1 : 0.3,
                        }}
                      />
                    </div>
                  </div>
                </div>
                <div className="mt-2 text-[10px] text-indigo-700/80">
                  Origine limiti attuali: <b>{resLimitsData.limits.source === "override" ? "Override agenzia" : resLimitsData.limits.source === "plan" ? `Piano "${resLimitsData.limits.plan_name || resLimitsData.plan?.name || ""}"` : resLimitsData.limits.source === "main" ? "Agenzia principale" : "Default piattaforma"}</b>
                </div>
              </div>
              <div>
                <label className="text-xs font-medium block mb-1">Piano assegnato</label>
                <select
                  data-testid="res-limits-plan"
                  className="w-full h-10 rounded-md border border-slate-300 px-3 text-sm bg-white"
                  value={resLimitsForm.plan_id || ""}
                  onChange={(e) => setResLimitsForm({ ...resLimitsForm, plan_id: e.target.value })}
                >
                  <option value="">— Nessun piano (usa default) —</option>
                  {pricingPlans.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.name} {p.max_clients ? `· ${p.max_clients} clienti` : "· clienti ∞"} {p.max_policies ? `· ${p.max_policies} polizze` : "· polizze ∞"}
                    </option>
                  ))}
                </select>
                <p className="text-[10px] text-slate-400 mt-0.5">
                  Selezionando un piano, i limiti di default vengono ereditati. Puoi comunque sovrascriverli sotto.
                </p>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                <div>
                  <label className="text-xs font-medium block mb-1">Max clienti (override)</label>
                  <Input
                    data-testid="res-limits-max-clients"
                    type="number"
                    min="0"
                    placeholder="Vuoto = illimitato"
                    value={resLimitsForm.max_clients}
                    onChange={(e) => setResLimitsForm({ ...resLimitsForm, max_clients: e.target.value })}
                  />
                </div>
                <div>
                  <label className="text-xs font-medium block mb-1">Max polizze (override)</label>
                  <Input
                    data-testid="res-limits-max-policies"
                    type="number"
                    min="0"
                    placeholder="Vuoto = illimitato"
                    value={resLimitsForm.max_policies}
                    onChange={(e) => setResLimitsForm({ ...resLimitsForm, max_policies: e.target.value })}
                  />
                </div>
                <div>
                  <label className="text-xs font-medium block mb-1">Max utenti (override)</label>
                  <Input
                    data-testid="res-limits-max-users"
                    type="number"
                    min="0"
                    placeholder="Vuoto = usa piano"
                    value={resLimitsForm.max_users}
                    onChange={(e) => setResLimitsForm({ ...resLimitsForm, max_users: e.target.value })}
                  />
                </div>
              </div>
              <button
                type="button"
                onClick={resetResLimitsOverride}
                className="text-[11px] text-slate-500 hover:text-slate-700 underline"
              >
                Rimuovi override e usa solo i limiti del piano
              </button>
              <p className="text-[10px] text-slate-400">
                Default piattaforma (nessun piano): {resLimitsData.defaults.max_clients} clienti / {resLimitsData.defaults.max_policies} polizze.
              </p>

              {/* 🆕 Sezione Storage */}
              <div className="border-t border-slate-200 pt-3 mt-2">
                <div className="text-sm font-semibold text-slate-800 mb-1 flex items-center gap-1.5">
                  💾 Spazio di archiviazione
                </div>
                <div className="rounded-md border border-slate-200 bg-slate-50 p-2 mb-2 text-xs">
                  <div className="flex items-center justify-between">
                    <span>Utilizzo attuale</span>
                    <span className="font-semibold tabular-nums">
                      <b>{resLimitsData.storage_used_mb ?? 0}</b> MB / {resLimitsData.storage_limit_mb ?? 500} MB
                    </span>
                  </div>
                  <div className="mt-1.5 h-1.5 rounded-full bg-slate-200 overflow-hidden">
                    <div
                      className={`h-full ${
                        (resLimitsData.storage_used_mb ?? 0) / (resLimitsData.storage_limit_mb || 1) >= 0.95
                          ? "bg-red-500"
                          : (resLimitsData.storage_used_mb ?? 0) / (resLimitsData.storage_limit_mb || 1) >= 0.8
                          ? "bg-amber-500"
                          : "bg-emerald-500"
                      }`}
                      style={{
                        width: `${Math.min(
                          100,
                          ((resLimitsData.storage_used_mb ?? 0) / (resLimitsData.storage_limit_mb || 1)) * 100,
                        )}%`,
                      }}
                    />
                  </div>
                </div>
                <div>
                  <label className="text-xs font-medium block mb-1">Limite storage (MB)</label>
                  <Input
                    data-testid="res-limits-storage-mb"
                    type="number"
                    min="1"
                    placeholder={`Vuoto = default (${resLimitsData.storage_default_mb ?? 500} MB)`}
                    value={resLimitsForm.storage_limit_mb}
                    onChange={(e) => setResLimitsForm({ ...resLimitsForm, storage_limit_mb: e.target.value })}
                  />
                  <p className="text-[10px] text-slate-400 mt-1">
                    Al 100% i nuovi upload verranno bloccati. Alert email automatico all&apos;80%.
                  </p>
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setResLimitsTarget(null)}>Annulla</Button>
            <Button
              onClick={doSaveResLimits}
              disabled={busy || resLimitsLoading}
              className="bg-indigo-600 hover:bg-indigo-700 text-white"
              data-testid="res-limits-save"
            >
              {busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}Salva
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {/* Dialog delete agency: unificato — sia soft-delete sia hard-delete passano da OTP.
          Step 1 = warning + invio OTP, Step 2 = input OTP. */}
      <Dialog open={!!deleteTarget} onOpenChange={(o) => !o && (setDeleteTarget(null), setDeleteStep(1), setDeleteOtp(""), setDeleteCounts(null))}>
        <DialogContent className="max-w-md" data-testid="delete-agency-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-rose-700">
              <Trash2 className="h-5 w-5" />
              {(viewMode === "trash" || deleteTarget?.status === "deleted" || deleteTarget?.deleted_at)
                ? `Elimina definitivamente ${deleteTarget?.name}`
                : `Sposta nel cestino: ${deleteTarget?.name}`}
            </DialogTitle>
          </DialogHeader>
          {deleteStep === 1 && deleteTarget && (
            <div className="space-y-3">
              {(viewMode === "trash" || deleteTarget.status === "deleted" || deleteTarget.deleted_at) ? (
                <div className="rounded-md border border-rose-300 bg-rose-50 p-3 text-sm text-rose-900">
                  <div className="font-semibold mb-1">⚠️ Azione irreversibile</div>
                  <p className="text-xs leading-relaxed">
                    Stai per eliminare definitivamente l&apos;agenzia <strong>{deleteTarget.name}</strong>
                    ({deleteTarget.owner_email}). Tutti i suoi dati saranno rimossi:
                  </p>
                  <ul className="text-xs mt-2 space-y-0.5 list-disc list-inside">
                    <li><b>{deleteTarget.users_count || 0}</b> utenti</li>
                    <li><b>{deleteTarget.clients_count || 0}</b> clienti</li>
                    <li><b>{deleteTarget.policies_count || 0}</b> polizze</li>
                    <li>Preventivi, offerte, allegati, chat, automazioni</li>
                  </ul>
                </div>
              ) : (
                <div className="rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900">
                  <div className="font-semibold mb-1">🗑️ Spostamento nel cestino</div>
                  <p className="text-xs leading-relaxed">
                    L&apos;agenzia <strong>{deleteTarget.name}</strong> ({deleteTarget.owner_email}) verrà spostata nel cestino.
                    Tutti gli utenti dell&apos;agenzia saranno <b>disattivati immediatamente</b>. I dati restano salvati e potrai ripristinarli in seguito.
                  </p>
                  <p className="text-xs mt-2">
                    Per sicurezza serve un codice OTP. Da lì potrai eliminarla definitivamente con un nuovo codice.
                  </p>
                </div>
              )}
              <p className="text-xs text-slate-600">
                Per procedere, riceverai un codice OTP via email. Inseriscilo per confermare.
              </p>
              <DialogFooter>
                <Button variant="outline" onClick={() => setDeleteTarget(null)}>Annulla</Button>
                <Button
                  data-testid="request-delete-otp"
                  onClick={requestDeleteOtp}
                  disabled={busy}
                  className="bg-rose-600 hover:bg-rose-700 text-white"
                >
                  {busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
                  Invia codice OTP via email
                </Button>
              </DialogFooter>
            </div>
          )}
          {deleteStep === 2 && (
            <div className="space-y-3">
              <div className="rounded-md border border-amber-300 bg-amber-50 p-3 text-xs text-amber-900">
                ✉️ Codice inviato. Controlla la tua casella email e inserisci le 6 cifre qui sotto. Valido 10 minuti.
              </div>
              {deleteCounts && (
                <div className="rounded bg-slate-50 border border-slate-200 p-2 text-[11px] text-slate-600">
                  Verranno eliminati: {deleteCounts.users} utenti · {deleteCounts.clients} clienti · {deleteCounts.policies} polizze · {deleteCounts.quotes} preventivi · {deleteCounts.quote_offers} offerte.
                </div>
              )}
              <div>
                <label className="text-xs font-medium block mb-1">Codice OTP (6 cifre)</label>
                <Input
                  data-testid="delete-otp-input"
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  maxLength={6}
                  autoComplete="one-time-code"
                  value={deleteOtp}
                  onChange={(e) => setDeleteOtp(e.target.value.replace(/[^0-9]/g, "").slice(0, 6))}
                  placeholder="000000"
                  className="font-mono text-center text-lg tracking-widest"
                />
              </div>
              <button
                type="button"
                data-testid="resend-delete-otp"
                onClick={requestDeleteOtp}
                disabled={busy}
                className="text-xs text-blue-600 hover:underline disabled:opacity-50"
              >
                Non hai ricevuto il codice? Invia di nuovo
              </button>
              <DialogFooter>
                <Button variant="outline" onClick={() => setDeleteTarget(null)}>Annulla</Button>
                <Button
                  data-testid="confirm-delete-agency"
                  onClick={confirmDelete}
                  disabled={busy || deleteOtp.length !== 6}
                  className="bg-rose-600 hover:bg-rose-700 text-white"
                >
                  {busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Trash2 className="h-4 w-4 mr-2" />}
                  Elimina definitivamente
                </Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>
      {/* Dialog Anagrafica Super-Admin */}
      <Dialog open={profileOpen} onOpenChange={(o) => !o && setProfileOpen(false)}>
        <DialogContent className="max-w-4xl max-h-[92vh] overflow-y-auto" data-testid="profile-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <UserCog className="h-5 w-5 text-violet-600" /> Anagrafica Super-Admin
            </DialogTitle>
          </DialogHeader>
          <p className="text-xs text-slate-500 -mt-2">
            Imposta i tuoi contatti. L&apos;email OTP viene usata per ricevere i codici di sicurezza;
            l&apos;email notifiche per ricevere i messaggi dalle nuove agenzie create.
          </p>
          {!profile ? (
            <div className="py-8 text-center text-slate-400 text-sm"><Loader2 className="h-5 w-5 inline animate-spin mr-2" /> Caricamento…</div>
          ) : (
            <div className="space-y-3">
              {/* Sezione 1: Contatti — 3 colonne */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <div>
                  <label className="text-xs font-medium block mb-1">Nome</label>
                  <Input data-testid="profile-name" value={profile.name || ""} onChange={(e) => setProfile({ ...profile, name: e.target.value })} placeholder="Mario Rossi" />
                </div>
                <div>
                  <label className="text-xs font-medium block mb-1">Email di login <span className="text-red-500">*</span></label>
                  <Input data-testid="profile-email" type="email" value={profile.email || ""} onChange={(e) => setProfile({ ...profile, email: e.target.value.toLowerCase() })} />
                  <p className="text-[10px] text-slate-400 mt-0.5">Usata per accedere al pannello.</p>
                </div>
                <div>
                  <label className="text-xs font-medium block mb-1">Cellulare</label>
                  <Input data-testid="profile-phone" value={profile.phone || ""} onChange={(e) => setProfile({ ...profile, phone: e.target.value })} placeholder="+39 333 1234567" />
                </div>
              </div>

              {/* Sezione 2: Email OTP + Notifiche — 2 colonne */}
              <div className="border-t border-slate-200 pt-3 grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-semibold block mb-1 text-violet-700">📨 Email per codici OTP</label>
                  <Input data-testid="profile-otp-email" type="email" value={profile.otp_email || ""} onChange={(e) => setProfile({ ...profile, otp_email: e.target.value.toLowerCase() })} placeholder="(default: email di login)" />
                  <p className="text-[10px] text-slate-500 mt-0.5">Dove ricevere i codici per operazioni critiche (es. eliminazione agenzia).</p>
                </div>
                <div>
                  <label className="text-xs font-semibold block mb-1 text-violet-700">🔔 Email per notifiche dalle agenzie</label>
                  <Input data-testid="profile-notif-email" type="email" value={profile.notifications_email || ""} onChange={(e) => setProfile({ ...profile, notifications_email: e.target.value.toLowerCase() })} placeholder="(default: email OTP o login)" />
                  <p className="text-[10px] text-slate-500 mt-0.5">Dove ricevere richieste di registrazione e notifiche pagamento delle agenzie.</p>
                </div>
              </div>

              {/* Sezione 3: Piattaforma — 2 colonne */}
              <div className="border-t border-slate-200 pt-3 grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-medium block mb-1">Ragione sociale piattaforma</label>
                  <Input data-testid="profile-company" value={profile.company_name || ""} onChange={(e) => setProfile({ ...profile, company_name: e.target.value })} placeholder="Es. Tua Azienda SRL" />
                </div>
                <div>
                  <label className="text-xs font-medium block mb-1">Indirizzo</label>
                  <Input data-testid="profile-address" value={profile.address || ""} onChange={(e) => setProfile({ ...profile, address: e.target.value })} placeholder="Via Roma 10, 00100 Roma" />
                </div>
              </div>

              {/* 🆕 Feb 2026 — SMTP di default della piattaforma (fallback per agenzie senza SMTP proprio) */}
              <div className="border-t border-slate-200 pt-3">
                <div className="flex items-center justify-between mb-1.5">
                  <label className="text-xs font-semibold text-violet-700 flex items-center gap-1.5">
                    📧 Configurazione SMTP di default (fallback piattaforma)
                  </label>
                  <label className="flex items-center gap-1.5 text-xs">
                    <input
                      type="checkbox"
                      data-testid="profile-smtp-enabled"
                      checked={!!profile.smtp_enabled}
                      onChange={(e) => setProfile({ ...profile, smtp_enabled: e.target.checked })}
                    />
                    <span>Attivo</span>
                  </label>
                </div>
                <p className="text-[10px] text-slate-500 mb-2">
                  Configurazione usata come <strong>fallback</strong> per l&apos;invio email (OTP, notifiche, reminder) quando l&apos;agenzia non ha un proprio SMTP configurato. Se disattivo o vuoto, verrà usato l&apos;SMTP delle variabili d&apos;ambiente come ultima risorsa.
                  {profile.smtp_updated_at && (
                    <> · <span className="italic">Ultima modifica: {new Date(profile.smtp_updated_at).toLocaleString("it-IT")}</span></>
                  )}
                </p>
                <div className={`space-y-2 ${profile.smtp_enabled ? "" : "opacity-60 pointer-events-none"}`}>
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-2">
                    <div>
                      <label className="text-[11px] block mb-0.5">Provider</label>
                      <select
                        data-testid="profile-smtp-provider"
                        value={profile.smtp_provider_type || "gmail"}
                        onChange={(e) => setProfile({ ...profile, smtp_provider_type: e.target.value })}
                        className="w-full h-9 border border-slate-300 rounded-md px-2 text-sm bg-white"
                      >
                        <option value="gmail">Gmail (App Password)</option>
                        <option value="smtp">SMTP generico</option>
                      </select>
                    </div>
                    <div>
                      <label className="text-[11px] block mb-0.5">Nome mittente</label>
                      <Input
                        data-testid="profile-smtp-display-name"
                        value={profile.smtp_display_name || ""}
                        onChange={(e) => setProfile({ ...profile, smtp_display_name: e.target.value })}
                        placeholder="Es. Assicura — Studio Globali"
                      />
                    </div>
                    <div>
                      <label className="text-[11px] block mb-0.5">Email mittente</label>
                      <Input
                        data-testid="profile-smtp-email"
                        type="email"
                        value={profile.smtp_email || ""}
                        onChange={(e) => setProfile({ ...profile, smtp_email: e.target.value.toLowerCase() })}
                        placeholder="studio.serviziglobali@gmail.com"
                      />
                    </div>
                    <div>
                      <label className="text-[11px] block mb-0.5">App Password (16 caratteri)</label>
                      <Input
                        data-testid="profile-smtp-password"
                        type="password"
                        value={profile.smtp_app_password || ""}
                        onChange={(e) => setProfile({ ...profile, smtp_app_password: e.target.value })}
                        placeholder="xxxx xxxx xxxx xxxx"
                        autoComplete="new-password"
                      />
                    </div>
                  </div>
                  <p className="text-[10px] text-slate-500 -mt-1">
                    Gmail: genera la App Password da <a href="https://myaccount.google.com/apppasswords" target="_blank" rel="noreferrer" className="text-violet-600 underline">myaccount.google.com/apppasswords</a> (richiede 2FA attiva).
                  </p>
                  {(profile.smtp_provider_type || "gmail") === "smtp" && (
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-2">
                      <div className="md:col-span-2">
                        <label className="text-[11px] block mb-0.5">Server SMTP (host)</label>
                        <Input
                          data-testid="profile-smtp-host"
                          value={profile.smtp_host || ""}
                          onChange={(e) => setProfile({ ...profile, smtp_host: e.target.value })}
                          placeholder="smtps.aruba.it"
                        />
                      </div>
                      <div>
                        <label className="text-[11px] block mb-0.5">Porta</label>
                        <Input
                          data-testid="profile-smtp-port"
                          type="number"
                          value={profile.smtp_port || ""}
                          onChange={(e) => setProfile({ ...profile, smtp_port: parseInt(e.target.value) || null })}
                          placeholder="465"
                        />
                      </div>
                      <div>
                        <label className="text-[11px] block mb-0.5">Username SMTP</label>
                        <Input
                          data-testid="profile-smtp-username"
                          value={profile.smtp_username || ""}
                          onChange={(e) => setProfile({ ...profile, smtp_username: e.target.value })}
                          placeholder="(opzionale)"
                        />
                      </div>
                    </div>
                  )}
                  <div className="flex justify-end pt-1">
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      data-testid="profile-smtp-test"
                      disabled={busy || !profile.smtp_enabled || !profile.smtp_email}
                      onClick={async () => {
                        setBusy(true);
                        try {
                          await api.put("/superadmin/profile", profile);
                          const { data } = await api.post("/superadmin/smtp/test");
                          toast.success(`Email di test inviata a ${data.recipient}`);
                        } catch (e) {
                          toast.error(e?.response?.data?.detail || "Errore invio test");
                        } finally { setBusy(false); }
                      }}
                    >
                      ✉️ Salva e invia email di test
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setProfileOpen(false)}>Annulla</Button>
            <Button data-testid="profile-save" onClick={saveProfile} disabled={busy || !profile} className="bg-violet-600 hover:bg-violet-700 text-white">
              {busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null} Salva anagrafica
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {/* Dialog review dati onboarding + finalize */}
      <Dialog open={!!reviewTarget} onOpenChange={(o) => !o && setReviewTarget(null)}>
        <DialogContent className="max-w-lg" data-testid="review-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Eye className="h-5 w-5 text-violet-600" /> Verifica dati ricevuti — {reviewTarget?.name}</DialogTitle>
          </DialogHeader>
          {reviewTarget?.onboarding_data ? (
            <div className="space-y-2 text-sm max-h-80 overflow-y-auto">
              {[
                ["Ragione sociale", reviewTarget.onboarding_data.legal_name],
                ["P.IVA", reviewTarget.onboarding_data.vat_number],
                ["Codice fiscale", reviewTarget.onboarding_data.fiscal_code],
                ["Indirizzo", `${reviewTarget.onboarding_data.address || ""}, ${reviewTarget.onboarding_data.cap || ""} ${reviewTarget.onboarding_data.city || ""} ${reviewTarget.onboarding_data.province ? "(" + reviewTarget.onboarding_data.province + ")" : ""}`.trim()],
                ["Telefono", reviewTarget.onboarding_data.phone],
                ["PEC", reviewTarget.onboarding_data.pec_email],
                ["RUI", reviewTarget.onboarding_data.rui_number],
                ["IBAN", reviewTarget.onboarding_data.iban],
                ["Note", reviewTarget.onboarding_data.notes],
              ].filter(([_, v]) => v && String(v).trim() && String(v).trim() !== ",").map(([k, v]) => (
                <div key={k} className="flex justify-between gap-3 border-b border-slate-100 pb-1.5">
                  <span className="text-xs text-slate-500 font-medium uppercase tracking-wide">{k}</span>
                  <span className="text-sm font-mono text-right truncate">{v}</span>
                </div>
              ))}
              {(reviewTarget.onboarding_data.documents || []).length > 0 && (
                <div className="pt-2">
                  <div className="text-xs text-slate-500 font-medium uppercase tracking-wide mb-1">Documenti caricati</div>
                  <ul className="space-y-1">
                    {reviewTarget.onboarding_data.documents.map((d) => (
                      <li key={d.id} className="flex items-center justify-between text-xs">
                        <span><FileText className="h-3 w-3 inline mr-1" /> {d.doc_type === "visura" ? "Visura" : d.doc_type === "id_card" ? "Documento ID" : "Altro"} — {d.filename}</span>
                        <a href={d.url} target="_blank" rel="noreferrer" className="text-blue-600 hover:underline">apri</a>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          ) : <p className="text-sm text-slate-400">Nessun dato ricevuto</p>}
          <div className="border-t border-slate-200 pt-3">
            <label className="text-xs font-medium block mb-1">Durata abbonamento (giorni)</label>
            <Input
              data-testid="finalize-days"
              type="number"
              min="1"
              max="3650"
              value={finalizeDays}
              onChange={(e) => setFinalizeDays(e.target.value)}
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setReviewTarget(null)}>Annulla</Button>
            <Button data-testid="finalize-confirm" onClick={finalizeActivation} disabled={busy} className="bg-violet-600 hover:bg-violet-700 text-white">
              {busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <CheckCircle2 className="h-4 w-4 mr-2" />}
              Attiva definitivamente
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {/* Dialog Inbox Settings IMAP */}
      {/* 🆕 Salute Inbox Agenzie — pannello controllo tecnico */}
      <Dialog open={inboxHealthOpen} onOpenChange={(o) => !o && setInboxHealthOpen(false)}>
        <DialogContent className="max-w-6xl max-h-[92vh] overflow-y-auto" data-testid="inbox-health-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <InboxIcon className="h-5 w-5 text-emerald-600" /> 🏥 Salute Inbox Agenzie
            </DialogTitle>
          </DialogHeader>
          {inboxHealthLoading ? (
            <div className="py-12 text-center"><Loader2 className="h-6 w-6 animate-spin inline text-emerald-600" /> <span className="ml-2 text-slate-600">Analisi in corso…</span></div>
          ) : !inboxHealth ? (
            <div className="py-6 text-center text-slate-500">Errore caricamento</div>
          ) : (
            <div className="space-y-4">
              {/* Summary cards */}
              <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
                {[
                  { key: "ok", label: "✅ Operative", color: "emerald" },
                  { key: "warning", label: "⚠ Warning", color: "amber" },
                  { key: "error", label: "❌ Errore", color: "rose" },
                  { key: "disabled", label: "⏸ Disattivate", color: "slate" },
                  { key: "not_configured", label: "🔧 Non config.", color: "purple" },
                ].map((s) => (
                  <button key={s.key} onClick={() => setInboxHealthFilter(s.key === inboxHealthFilter ? "all" : s.key)}
                          data-testid={`inbox-health-filter-${s.key}`}
                          className={`rounded-lg border p-3 text-left transition ${
                            inboxHealthFilter === s.key
                              ? `bg-${s.color}-100 border-${s.color}-500 ring-2 ring-${s.color}-200`
                              : `bg-white border-slate-200 hover:bg-${s.color}-50`
                          }`}>
                    <div className={`text-2xl font-bold text-${s.color}-700 tabular-nums`}>{inboxHealth.summary?.[s.key] || 0}</div>
                    <div className="text-[11px] text-slate-600">{s.label}</div>
                  </button>
                ))}
              </div>
              <div className="flex items-center justify-between">
                <span className="text-xs text-slate-500">Totale agenzie: <strong>{inboxHealth.summary?.total || 0}</strong> · Generato: {inboxHealth.generated_at ? new Date(inboxHealth.generated_at).toLocaleString("it-IT") : "-"}</span>
                <div className="flex gap-2">
                  {inboxHealthFilter !== "all" && (
                    <Button size="sm" variant="ghost" onClick={() => setInboxHealthFilter("all")}>❌ Rimuovi filtro</Button>
                  )}
                  <Button size="sm" variant="outline" onClick={openInboxHealth}><RotateCcw className="h-3 w-3 mr-1" /> Ricarica</Button>
                </div>
              </div>
              {/* Tabella agenzie */}
              <div className="border border-slate-200 rounded-lg overflow-x-auto">
                <table className="w-full text-xs" data-testid="inbox-health-table">
                  <thead className="bg-slate-50 text-slate-700">
                    <tr>
                      <th className="px-3 py-2 text-left">Agenzia</th>
                      <th className="px-3 py-2 text-left">Stato</th>
                      <th className="px-3 py-2 text-left">Host / User</th>
                      <th className="px-3 py-2 text-left">Ultimo polling</th>
                      <th className="px-3 py-2 text-right" title="Email ricevute negli ultimi 30 giorni">📥 30gg</th>
                      <th className="px-3 py-2 text-right" title="Email inviate negli ultimi 30 giorni">📤 30gg</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(inboxHealth.agencies || [])
                      .filter((a) => inboxHealthFilter === "all" || a.status === inboxHealthFilter)
                      .map((a) => {
                        const statusColors = {
                          ok: "bg-emerald-100 text-emerald-800 border-emerald-300",
                          warning: "bg-amber-100 text-amber-800 border-amber-300",
                          error: "bg-rose-100 text-rose-800 border-rose-300",
                          disabled: "bg-slate-100 text-slate-700 border-slate-300",
                          not_configured: "bg-purple-100 text-purple-800 border-purple-300",
                        };
                        return (
                          <tr key={a.agency_id} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`inbox-health-row-${a.agency_id}`}>
                            <td className="px-3 py-2">
                              <div className="font-semibold text-slate-900">{a.agency_name}</div>
                              <div className="text-[10px] text-slate-500">{a.agency_email || "-"} {a.agency_phone && `· ${a.agency_phone}`}</div>
                            </td>
                            <td className="px-3 py-2">
                              <span className={`inline-block text-[10px] px-1.5 py-0.5 rounded border ${statusColors[a.status] || "bg-slate-100"}`}>
                                {a.status_label}
                              </span>
                              {a.last_error && (
                                <div className="mt-0.5 text-[9px] text-rose-700 truncate max-w-[240px]" title={a.last_error}>{a.last_error}</div>
                              )}
                            </td>
                            <td className="px-3 py-2 text-slate-700">
                              {a.imap_host || "-"}
                              <div className="text-[10px] text-slate-500 truncate max-w-[200px]">{a.imap_user || "—"} {a.imap_pass_set ? "🔒" : "🔓"}</div>
                            </td>
                            <td className="px-3 py-2 text-slate-700">
                              {a.last_polled_at ? new Date(a.last_polled_at).toLocaleString("it-IT") : <span className="text-slate-400">mai</span>}
                              {typeof a.last_polled_count === "number" && (
                                <div className="text-[10px] text-slate-500">{a.last_polled_count} email nuove</div>
                              )}
                            </td>
                            <td className="px-3 py-2 text-right tabular-nums font-medium">{a.inbox_30d || 0}</td>
                            <td className="px-3 py-2 text-right tabular-nums font-medium">{a.outbox_30d || 0}</td>
                          </tr>
                        );
                      })}
                  </tbody>
                </table>
                {(inboxHealth.agencies || []).filter((a) => inboxHealthFilter === "all" || a.status === inboxHealthFilter).length === 0 && (
                  <div className="p-6 text-center text-slate-400 text-sm">Nessuna agenzia con lo stato selezionato.</div>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={inboxOpen} onOpenChange={(o) => !o && setInboxOpen(false)}>
        <DialogContent className="max-w-lg" data-testid="inbox-settings-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Mail className="h-5 w-5 text-violet-600" /> Email di Sistema
            </DialogTitle>
          </DialogHeader>
          <div className="text-xs text-slate-600 -mt-2 space-y-1">
            <p>
              Questa configurazione serve <strong>esclusivamente</strong> per l&apos;invio di:
            </p>
            <ul className="list-disc list-inside text-[11px] text-slate-500 space-y-0.5 ml-1">
              <li>🔐 Codici OTP di sicurezza</li>
              <li>📢 Notifiche di sistema (renewal SaaS, promemoria, avvisi tecnici)</li>
              <li>📨 Email inter-piattaforma verso gli admin delle agenzie</li>
            </ul>
            <p className="text-[11px] text-slate-500 pt-1">
              🏢 La <strong>ricezione</strong> delle email di ogni agenzia (IMAP) è configurata dagli admin
              di ciascuna agenzia in <em>Impostazioni Email → Ricezione (IMAP)</em>, non da qui.
            </p>
          </div>
          <div className="rounded-md border border-slate-200 bg-slate-50 p-3 text-xs space-y-1.5">
            <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Stato SMTP piattaforma</div>
            <div>Host: <code className="bg-white px-1.5 py-0.5 rounded border text-[11px]">smtp.gmail.com:465 (SSL)</code></div>
            <div>Configurato via variabili d&apos;ambiente <code className="text-[10px]">SMTP_USER / SMTP_PASS</code> del server</div>
            <div className="text-[10px] text-slate-500 pt-1">💡 Per modificare host/porta/credenziali della piattaforma, aggiorna le env vars nel deploy e riavvia. In caso di dubbi contatta il supporto.</div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setInboxOpen(false)}>Chiudi</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>


      {/* 🆕 Feb 2026 — Dettaglio Accessi (login monitor) */}
      <Dialog open={!!accessLogTarget} onOpenChange={(o) => { if (!o) { setAccessLogTarget(null); setAccessLog(null); } }}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="access-log-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Eye className="h-5 w-5 text-cyan-600" />
              Dettaglio Accessi · {accessLogTarget?.name}
            </DialogTitle>
          </DialogHeader>
          {accessLogLoading ? (
            <div className="py-10 text-center text-slate-500 flex items-center justify-center gap-2">
              <Loader2 className="h-4 w-4 animate-spin" /> Caricamento...
            </div>
          ) : accessLog ? (
            <div className="space-y-5">
              {/* Utenti online */}
              <section>
                <h3 className="text-sm font-semibold text-slate-800 flex items-center gap-2 mb-2">
                  <span className="inline-flex h-2 w-2 rounded-full bg-emerald-500 animate-pulse" />
                  Utenti online adesso
                  <span className="text-xs font-normal text-slate-400">({accessLog.online_users?.length || 0})</span>
                </h3>
                {(accessLog.online_users || []).length === 0 ? (
                  <p className="text-xs text-slate-400 italic px-2">Nessun utente online (attività negli ultimi 5 minuti).</p>
                ) : (
                  <div className="border border-emerald-200 rounded-md overflow-hidden">
                    <table className="w-full text-xs">
                      <thead className="bg-emerald-50 text-emerald-800">
                        <tr>
                          <th className="text-left px-3 py-1.5">Utente</th>
                          <th className="text-left px-3 py-1.5">Ruolo</th>
                          <th className="text-right px-3 py-1.5">Ultima attività</th>
                        </tr>
                      </thead>
                      <tbody>
                        {accessLog.online_users.map((u) => (
                          <tr key={u.id} className="border-t border-emerald-100" data-testid={`online-user-${u.id}`}>
                            <td className="px-3 py-1.5">
                              <div className="font-medium text-slate-800">{u.email}</div>
                              <div className="text-[10px] text-slate-500 flex items-center gap-1.5">
                                {u.name}
                                {u.is_demo_access && (
                                  <span className="inline-block px-1 py-0 rounded bg-amber-100 text-amber-700 border border-amber-200 text-[9px] font-semibold">DEMO</span>
                                )}
                              </div>
                            </td>
                            <td className="px-3 py-1.5">
                              <span className="inline-block px-1.5 py-0.5 rounded bg-slate-100 text-slate-700 text-[10px] font-medium capitalize">
                                {u.role || "—"}
                              </span>
                            </td>
                            <td className="px-3 py-1.5 text-right text-slate-600 tabular-nums">
                              {u.last_seen_at ? new Date(u.last_seen_at).toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit" }) : "—"}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </section>
              {/* Ultimi 20 accessi */}
              <section>
                <h3 className="text-sm font-semibold text-slate-800 mb-2">
                  Ultimi 20 accessi
                  <span className="text-xs font-normal text-slate-400 ml-1">({accessLog.recent_logins?.length || 0})</span>
                </h3>
                {(accessLog.recent_logins || []).length === 0 ? (
                  <p className="text-xs text-slate-400 italic px-2">Nessun login registrato per questa agenzia.</p>
                ) : (
                  <div className="border border-slate-200 rounded-md overflow-hidden">
                    <table className="w-full text-xs">
                      <thead className="bg-slate-50 text-slate-600">
                        <tr>
                          <th className="text-left px-3 py-1.5">Utente</th>
                          <th className="text-left px-3 py-1.5">Ruolo</th>
                          <th className="text-right px-3 py-1.5">Data e ora</th>
                        </tr>
                      </thead>
                      <tbody>
                        {accessLog.recent_logins.map((ev) => {
                          const dt = ev.logged_at ? new Date(ev.logged_at) : null;
                          return (
                            <tr key={ev.id} className="border-t border-slate-100" data-testid={`login-event-${ev.id}`}>
                              <td className="px-3 py-1.5">
                                <div className="font-medium text-slate-800">{ev.user_email}</div>
                                <div className="text-[10px] text-slate-500">{ev.user_name}</div>
                              </td>
                              <td className="px-3 py-1.5">
                                <span className="inline-block px-1.5 py-0.5 rounded bg-slate-100 text-slate-700 text-[10px] font-medium capitalize">
                                  {ev.user_role || "—"}
                                </span>
                              </td>
                              <td className="px-3 py-1.5 text-right text-slate-600 tabular-nums">
                                {dt ? dt.toLocaleString("it-IT", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "—"}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </section>
            </div>
          ) : (
            <p className="text-xs text-slate-400 italic py-6 text-center">Nessun dato disponibile.</p>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => openAccessLog(accessLogTarget)} disabled={accessLogLoading} data-testid="access-log-refresh">
              🔄 Aggiorna
            </Button>
            <Button variant="outline" onClick={() => { setAccessLogTarget(null); setAccessLog(null); }} data-testid="access-log-close">
              Chiudi
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>


      </div>
    </div>
  );
}
